import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useRememberedState } from '../../hooks/useRemembered';
import { AppSettings, UserProfile, ReportMetadata, ServiceItem, Report, ServiceTransaction } from '../../types';
import { Button } from '../Button';
import { Wallet, X, CheckCircle, Download, Filter, History, ArrowRight, RotateCcw, Search, FileText, CheckCircle2, ChevronDown, ChevronUp, Scale, CalendarClock, Lock } from 'lucide-react';
import { BUCHHALTUNG_NUR_LESEN_HINWEIS, buchhaltungAktionen } from '../invoices/rechnungRechte';
import { loadProjectById, saveProjectToCloud, fetchReportHistory, deleteReportHistory } from '../../services/cloudService';
import { accountingOpenQuantity, bestandteilSperrText, getNormalizedServices, getProjectFinancials, hasOpenBalance, heldByComponentsQuantity, isHourlyUnit, openQuantity, waivedQuantity } from '../../services/projectLv';
import { istAbrechnungGesperrt, paketDeckel } from '../../services/bestandteile/bestandteilLogic';
import { flatCoverageHints } from '../../services/flatRateLogic';
import { isNepService, NEP_BADGE } from '../../services/nepPosition';
import { resolveInvoiceConfig } from '../../services/invoiceConfig';
import { ExternalAdvancesPanel } from '../invoices/ExternalAdvancesPanel';
import { SplitKeyEditor } from '../invoices/SplitKeyEditor';
import { fetchProjectSplit } from '../../services/projectSplitService';
import {
    assignUnassignedBilled,
    billedByParty,
    legacyPartyByCustomer,
} from '../../services/projectSplit';
import { fetchCustomers } from '../../services/crmService';
import { mirrorEntriesToJournal, voidJournalEntries } from '../../services/serviceJournal';
import { spiegleZuordnungsUmzug } from '../../services/backfillMoveJournal';
import { removeServiceLogEntries } from '../../services/serviceLogRemoval';
import { billedAfterPositionRestore, istBelegBuchung, planPositionRestore, stampLegacyEntries } from '../../services/serviceLogUtils';
import { describeError } from '../../services/errorMessage';
import {
    altCoverageByService,
    applyBackfillIncrements,
    backfillEffectiveIncrements,
    stampAppliedIncrements,
    BACKFILL_REFERENCE_PREFIX,
    BackfillSelection,
    backfillQuantityIncrements,
    buildBackfillServices,
    missingBackfillTargets,
    moveAltBackfill,
    revertBackfill,
} from '../../services/externalAdvances';
import { isProjectArchived, isProjectFinished } from '../../services/projectBilling';
import { ladeBelegZustaendeJeProjekt, ladeOffeneBelegeJeProjekt } from '../../services/archiveGuardService';
import { BelegZustand, SchlussrechnungsStand, offenerBelegStand, schlussrechnungsStand } from '../../services/archiveGuardLogic';
import { waiveServiceQuantity, releaseServiceWaiver } from '../../services/serviceWaiverService';
import { ServiceWaiverDialog } from './ServiceWaiverDialog';
import { FlatRateSwitchAssistant } from './FlatRateSwitchAssistant';
import { reverseServiceBooking } from '../../services/serviceReversalService';
import { ReversalTarget } from '../../services/serviceReversalLogic';
import { ServiceReversalDialog } from './ServiceReversalDialog';
import { bookManualBilling, undoManualBilling } from '../../services/manualBillingService';
import { manuallyBillableQuantity, manuallyBilledQuantity } from '../../services/manualBillingLogic';
import { ManualBillingDialog } from './ManualBillingDialog';
import { HistoryView } from '../HistoryView';
import { PdfPreviewModal } from '../PdfPreviewModal';
import { PerformanceProofEditor } from '../PerformanceProofEditor';
import { brauchtLiveArchivEintrag, liveArchivEintrag } from '../../services/archivLiveEintrag';
import type { ListProject } from '../../services/listProject';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface Props {
  projects: ListProject[];
  currentUser: UserProfile | null;
  onRefresh: () => void;
  // Sprung Buchhaltung → Projekt (öffnet den ProjectHub)
  onOpenProject?: (project: ListProject) => void;
  // Deep-Link Projekt → Buchhaltung: öffnet die Abrechnung dieses Projekts direkt
  initialProjectId?: string;
  /**
   * Weg zur Rechnung. Gebucht wird ausschließlich dort — nur eine Rechnung
   * erzeugt Nummer, Festschreibung und Archiv. Zwei Wege, die quantityBilled
   * schreiben, wären ein Doppelbuchungspfad.
   */
  onBillProject?: (projectId: string) => void;
  /**
   * Lesemodus (Rollensystem, rechnungRechte.buchhaltungNurLesen): ohne Recht
   * „Rechnungen bearbeiten“ ist jeder Schreibweg ausgeblendet oder gesperrt,
   * auch in den Dialogen, die von hier aus aufgehen. Lesen, Filtern,
   * Springen und PDFs bleiben. Den Projektstand, in den diese Ansicht
   * schreibt, schützt keine Datenbankregel — die Sperre hier ist die einzige.
   */
  nurLesen?: boolean;
  /**
   * Steuersatz neuer Alt-Abschläge aus den Rechnungs-Grundeinstellungen der
   * FIRMA. Fehlt er, gilt der aus dem eigenen Profil — das trägt ihn nur
   * beim Admin, der die Einstellungen pflegt.
   */
  defaultTaxRateBp?: number;
}

// Stand der Schlussrechnung bei BEENDET: gruen = an den Belegen haengt nichts
// mehr, gelb = Geld steht aus, indigo = die Buchhaltung ist dran.
const FINAL_STAND_STYLE: Record<SchlussrechnungsStand['stufe'], string> = {
    erledigt: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    keine_belege: 'bg-slate-50 text-slate-600 border-slate-200',
    unbezahlt: 'bg-amber-50 text-amber-800 border-amber-200',
    beleg_offen: 'bg-amber-50 text-amber-800 border-amber-200',
    entwurf: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    fehlt: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    unbekannt: 'bg-slate-50 text-slate-400 border-slate-200',
};
const FINAL_STAND_HINT: Record<SchlussrechnungsStand['stufe'], string> = {
    erledigt: 'Belege erledigt. Ein Administrator kann das Projekt ins Archiv setzen (Projektstatus), sofern nichts anderes sperrt (offene Posten, Tochterprojekte); dann verschwindet es aus dieser Liste.',
    keine_belege: 'Im Rechnungsmodul gibt es keine Belege zu diesem Projekt (Altbestand oder außerhalb abgerechnet). Sperrt nichts anderes (offene Posten, Tochterprojekte), kann ein Administrator es ins Archiv setzen.',
    unbezahlt: 'Die Schlussrechnung ist gestellt. Das Projekt bleibt hier, bis sie bezahlt ist und ein Administrator es ins Archiv setzt.',
    beleg_offen: 'Ein gestellter Beleg ist noch nicht bezahlt. Erst danach ist das Archiv möglich.',
    entwurf: 'Die Schlussrechnung liegt als Entwurf im Rechnungsmodul und ist noch nicht gestellt.',
    fehlt: 'Projekt beendet: Schlussrechnung stellen, auch wenn sie nur die Abschläge zusammenfasst.',
    unbekannt: 'Die Belege konnten nicht geladen werden.',
};
/** Hinweis fuer ein ARCHIV mit unbezahltem Beleg — dort passt keiner der Texte oben. */
const ARCHIV_BELEG_HINT = 'Das Projekt liegt im Archiv, aber ein gestellter Beleg ist noch nicht bezahlt. Es bleibt hier, bis die Zahlung im Rechnungsmodul gebucht ist.';

/** Plakette „Nur lesen“ — oben in der Übersicht und im Kopf der Abrechnung. */
const NurLesenPlakette: React.FC<{ aufFarbe?: boolean }> = ({ aufFarbe = false }) => (
    <span
        title={BUCHHALTUNG_NUR_LESEN_HINWEIS}
        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
            aufFarbe ? 'border-white/30 bg-white/15 text-white' : 'border-amber-200 bg-amber-50 text-amber-700'
        }`}
    >
        <Lock className="w-3 h-3" /> Nur lesen
    </span>
);

export const AdminAccountingView: React.FC<Props> = ({ projects, currentUser, onRefresh, onOpenProject, initialProjectId, onBillProject, nurLesen = false, defaultTaxRateBp }) => {
    // Rollensystem: welche Schreibwege offen sind. Im Lesemodus keiner.
    const darf = useMemo(() => buchhaltungAktionen(nurLesen), [nurLesen]);
    const [selectedProjectForAccounting, setSelectedProjectForAccounting] = useState<ListProject | null>(null);
    const [searchTerm, setSearchTerm] = useRememberedState('accounting.search', '');
    const [minBillableFilter, setMinBillableFilter] = useRememberedState<number>('accounting.minBillable', 0);
    const [onlyBillableFilter, setOnlyBillableFilter] = useRememberedState('accounting.onlyBillable', false);
    const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
    const [showAccountingArchive, setShowAccountingArchive] = useRememberedState('accounting.archive', false);
    
    // Performance Proof Editor (replaces old pdfExportModal)
    const [proofEditorProject, setProofEditorProject] = useState<any | null>(null);

    // Billing Modal
    const [billingModalOpen, setBillingModalOpen] = useState(false);
    const [billingValues, setBillingValues] = useState<Record<string, number>>({});

    // History & PDF Preview States
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [projectHistory, setProjectHistory] = useState<any[]>([]);
    // Hinweis oben im Archiv-Dialog, z. B. wenn der aktuelle Stand nicht geladen werden konnte.
    const [historyHinweis, setHistoryHinweis] = useState<string | null>(null);
    const [previewReport, setPreviewReport] = useState<any | null>(null);
    const [showPdfPreview, setShowPdfPreview] = useState(false);

    /**
     * Firmenstammdaten fuer die Archiv-Vorschau.
     *
     * Ohne settings-Prop erzeugte PdfPreviewModal den Bericht ohne Logo,
     * Firmenname und Fusszeile — die Vorschau zeigte etwas anderes als das
     * ausgelieferte PDF. Der archivierte Bericht bringt seinen eigenen Stand
     * mit (so wurde er versendet); nur wenn er keinen hat (Altbestand), treten
     * die heutigen Stammdaten ein.
     */
    const previewSettings = useMemo<AppSettings | undefined>(() => {
        const archived = previewReport?.settings as AppSettings | undefined;
        if (archived) return archived;
        if (!currentUser) return undefined;
        const p = currentUser as any;
        return {
            companyName: p.company_name || '',
            companyAddress: p.company_address || '',
            companyLogo: p.company_logo || '',
            appMode: p.app_mode || 'sigeko',
            footerText: p.settings?.footerText,
        };
    }, [previewReport, currentUser]);

    /**
     * Position, fuer die der Verzichtsdialog offen ist ('nicht mehr
     * abrechnen'). Gehalten wird die KENNUNG und nicht die Position: nach
     * dem Speichern laedt onRefresh die Liste neu, ein festgehaltenes
     * Objekt zeigte danach den alten Mengenstand.
     */
    const [waiverServiceId, setWaiverServiceId] = useState<string | null>(null);
    /**
     * Position, fuer die der Ruecknahmedialog offen ist. Bewusst ein
     * ZWEITER Zustand neben dem Verzicht: die beiden Vorgaenge duerfen sich
     * nicht wie zwei Schalter desselben Dialogs anfuehlen, weil der
     * Unterschied zwischen ihnen genau der teure ist.
     */
    const [reversalServiceId, setReversalServiceId] = useState<string | null>(null);
    /**
     * UMSTELLUNGS-ASSISTENT Monatspauschale. Wie Verzicht und Ruecknahme
     * haengt er an der Abrechnungs-Detailansicht — hier stehen die offenen
     * Restmengen, um die sich sein harter Riegel dreht. Regeln in
     * flatRateSwitchLogic, Schreiben in flatRateSwitchService.
     */
    const [flatSwitchOpen, setFlatSwitchOpen] = useState(false);
    /**
     * Position, fuer die die MANUELLE VERBUCHUNG offen ist ('als abgerechnet
     * buchen'). Der dritte eigene Zustand aus demselben Grund wie der zweite:
     * Verbuchen, Verzichten und Zuruecknehmen duerfen sich nicht wie drei
     * Schalter desselben Dialogs anfuehlen — der Unterschied zwischen ihnen
     * ist der teure.
     */
    const [manualBillingServiceId, setManualBillingServiceId] = useState<string | null>(null);
    const [showBookingLog, setShowBookingLog] = useState(false);
    /**
     * Steht der Aufteilungsschluessel dieses Projekts zur Bearbeitung offen?
     *
     * Nur ein Schalter und keine Kennung: der Editor bearbeitet den Schluessel
     * des GANZEN Projekts und nicht den einer einzelnen Position — anders als
     * Verzicht und Ruecknahme daneben, die je Position wirken.
     */
    const [splitEditorOpen, setSplitEditorOpen] = useState(false);
    /**
     * Beteiligte des Aufteilungsschluessels — mit Namen, denn das Einlesen
     * fragt nach dem AUFTRAGGEBER, und eine Kundenkennung ist keine Antwort,
     * die jemand geben kann.
     *
     * Wird auch nach dem Schliessen des Schluessel-Editors neu geladen: wer
     * dort gerade einen Beteiligten ergaenzt hat, soll ihn unmittelbar danach
     * im Einlese-Dialog waehlen koennen.
     */
    const [splitParticipants, setSplitParticipants] = useState<
        { partyId: string; customerId: string; name: string }[] | null
    >(null);
    /**
     * customerId -> partyId, nur wo eindeutig (projectSplit.legacyPartyByCustomer).
     *
     * Gebraucht, um Protokollzeilen aus der Zeit VOR den Abschnitten weiterhin
     * einem Teilnehmer zuzurechnen. Ohne die Karte zaehlte in einem bestehenden,
     * nach Auftraggebern geteilten Projekt jede Altzeile als nicht zugeordnet —
     * die Ansicht meldete plötzlich Altbestand, den es nie gab.
     */
    const [splitLegacy, setSplitLegacy] = useState<ReadonlyMap<string, string>>(new Map());
    const [expandedReceipt, setExpandedReceipt] = useRememberedState<string | null>('accounting.expandedReceipt', null);
    useState<string | null>(null);
    useState<string>('');

    const getLocalDateStr = () => {
        const d = new Date();
        const offset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - offset).toISOString().split('T')[0];
    };

    const [billingDate, setBillingDate] = useState(getLocalDateStr());

    // Deep-Link aus dem ProjectHub: Abrechnung des Projekts direkt öffnen.
    // Ref verhindert Wieder-Öffnen nach manuellem Schließen (projects-Refetches).
    const consumedFocusRef = useRef<string | null>(null);
    useEffect(() => {
        if (!initialProjectId || consumedFocusRef.current === initialProjectId) return;
        const p = projects.find(x => x.id === initialProjectId);
        if (p) { consumedFocusRef.current = initialProjectId; setSelectedProjectForAccounting(p); setSelectedServices(new Set()); }
    }, [initialProjectId, projects]);
    const formatCurrency = (amount: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

    // getNormalizedServices / getProjectFinancials liegen seit dem Rechnungsmodul
    // in services/projectLv.ts: dieselbe Logik, aber testbar und auch fuer
    // Rechnungslauf und Projektcheck erreichbar. Verhalten unveraendert.

    // Offene Belege der Flotte, je Projekt (ladeOffeneBelegeJeProjekt).
    // `null` = noch nicht geladen oder nicht ladbar: dann gilt die Liste wie
    // vor dieser Regel, und das Archiv steht in der Historie.
    const [offeneBelege, setOffeneBelege] = useState<Map<string, BelegZustand[]> | null>(null);

    const filteredAccountingProjects = useMemo(() => {
        return projects.map(p => ({ ...p, financials: getProjectFinancials(p) })).filter(p => {
             const meta = p.data?.metadata as ReportMetadata | undefined;
             if (meta?.globalData?.linkedProjectConfig?.role === 'child') return false;
             // ARCHIV ohne offenes Geld ist erledigt und verschwindet. BEENDET
             // bleibt IMMER stehen, auch ohne offene Menge: dort ist die
             // Schlussrechnung zu stellen, und die kann auch nur Abschlaege
             // abrechnen. Erst das Archiv (nach bezahlter Schlussrechnung)
             // nimmt das Projekt hier heraus.
             const isArchived = isProjectArchived(p);
             const isCompleted = isProjectFinished(p) && !isArchived;
             // ARCHIV mit unbezahltem Beleg (vor Regel 3 archiviert, Fall
             // 6271): das Geld steht aus, also bleibt es wie BEENDET stehen —
             // auch gegen „Nur Abrechenbare", denn abrechenbar ist dort nichts.
             const archivMitOffenemBeleg = isArchived && (offeneBelege?.get(p.id)?.length ?? 0) > 0;
             // PAKETPOSITION: zurueckgehaltenes Geld (Bestandteile offen) ist
             // kein abrechenbares, aber auch kein erledigtes — das Archiv
             // bleibt dann in der Liste (hasOpenBalance statt open <= 0).
             if (isArchived && !hasOpenBalance(p.financials) && !archivMitOffenemBeleg) return false;
             if (searchTerm) { const lower = searchTerm.toLowerCase(); if (!(p.name || '').toLowerCase().includes(lower) && !(p.project_number || p.data?.metadata?.projectNumber || '').toLowerCase().includes(lower)) return false; }
             if (isCompleted || archivMitOffenemBeleg) return true;
             if (onlyBillableFilter && p.financials.open <= 0) return false;
             if (p.financials.open < minBillableFilter) return false;
             return true;
        }).sort((a,b) => {
            const fa = isProjectFinished(a);
            const fb = isProjectFinished(b);
            if (fa && !fb) return -1; if (!fa && fb) return 1;
            return b.financials.open - a.financials.open;
        });
    }, [projects, onlyBillableFilter, minBillableFilter, searchTerm, offeneBelege]);

    // BEENDET steht auch ohne offenen Betrag in der Liste (siehe oben). Ohne
    // den Stand der Schlussrechnung saehe so eine Zeile aus wie ein Fehler —
    // deshalb die Belege dieser Projekte, gesammelt in einer Abfrage. Neu
    // geladen, wenn sich die Menge aendert oder die Detailansicht schliesst
    // (dort kann eine Rechnung entstanden sein).
    const completedIdsKey = useMemo(() => filteredAccountingProjects
        .filter(p => isProjectFinished(p) && !isProjectArchived(p))
        .map(p => p.id)
        .sort()
        .join(','), [filteredAccountingProjects]);
    const detailOpen = selectedProjectForAccounting !== null;
    // Der Stand haengt an der Projektmenge, fuer die er geladen wurde (`key`).
    const [finalInvoiceStates, setFinalInvoiceStates] = useState<{ key: string; belege: Map<string, BelegZustand[]> | null } | null>(null);
    useEffect(() => {
        if (detailOpen) return;
        // Alten Stand verwerfen, BEVOR neu geladen wird: sonst zeigte die Liste
        // nach einer Rechnung im Detailpanel bis zur Antwort den vorigen Stand.
        setFinalInvoiceStates(null);
        if (!completedIdsKey) return;
        let cancelled = false;
        ladeBelegZustaendeJeProjekt(completedIdsKey.split(',')).then(belege => {
            if (!cancelled) setFinalInvoiceStates({ key: completedIdsKey, belege });
        });
        return () => { cancelled = true; };
    }, [completedIdsKey, detailOpen]);
    // Neu geladen, wenn die Detailansicht schliesst (dort kann eine Zahlung
    // gebucht worden sein, dann geht das Archiv zurueck in die Historie) und
    // mit jeder neuen Projektliste (Aktualisieren).
    useEffect(() => {
        if (detailOpen) return;
        let cancelled = false;
        ladeOffeneBelegeJeProjekt().then(belege => {
            if (!cancelled) setOffeneBelege(belege);
        });
        return () => { cancelled = true; };
    }, [detailOpen, projects]);
    const finalInvoiceStandFor = (projectId: string) => {
        // Laedt noch oder stammt aus einer anderen Projektmenge (Filter
        // gewechselt): nichts zeigen statt eines falschen „nicht ladbar".
        if (!finalInvoiceStates || finalInvoiceStates.key !== completedIdsKey) return null;
        return schlussrechnungsStand(finalInvoiceStates.belege === null ? null : finalInvoiceStates.belege.get(projectId) ?? null);
    };

    const finishedProjects = useMemo(() => {
        return projects.map(p => ({ ...p, financials: getProjectFinancials(p) })).filter(p => {
             const meta = p.data?.metadata as ReportMetadata | undefined;
             if (meta?.globalData?.linkedProjectConfig?.role === 'child') return false;
             // Die Historie zeigt das ARCHIV: Schlussrechnung gestellt und bezahlt.
             // Mit zurueckgehaltenem Paketgeld oder unbezahltem Beleg steht es
             // oben in der Liste, nicht hier — sonst stuende es an beiden
             // Stellen oder an keiner.
             if (!isProjectArchived(p) || hasOpenBalance(p.financials) || (offeneBelege?.get(p.id)?.length ?? 0) > 0) return false;
             if (searchTerm) { const lower = searchTerm.toLowerCase(); return (p.name || '').toLowerCase().includes(lower) || (p.project_number || '').toLowerCase().includes(lower); }
             return true;
        });
    }, [projects, searchTerm, offeneBelege]);

    const archivedPositions = useMemo(() => {
        const list: any[] = [];
        projects.forEach(p => {
            const meta = p.data?.metadata as ReportMetadata | undefined;
            if (meta?.globalData?.linkedProjectConfig?.role === 'child') return;
            const log = meta?.globalData?.serviceLog || [];
            getNormalizedServices(p).forEach((s: ServiceItem) => {
                // Stammt die Buchung aus dem Rechnungsmodul, gibt es einen Beleg
                // dazu. Zuruecknehmen darf sie dann nur ein Storno.
                const hasInvoiceBooking = log.some(
                    (t) => t.serviceId === s.id && /^BU-(INV|STO)-/.test(t.bookingReference || '')
                );
                if ((s.quantityBilled || 0) > 0) list.push({ uniqueId: `${p.id}_${s.id}`, projectId: p.id, projectName: p.name, projectNumber: p.project_number || p.data?.metadata?.projectNumber, service: s, billedAmount: (s.quantityBilled || 0) * s.unitPrice, billedQty: s.quantityBilled, hasInvoiceBooking });
            });
        });
        return list.filter(item => !searchTerm || item.projectName.toLowerCase().includes(searchTerm.toLowerCase()) || (item.projectNumber || '').toLowerCase().includes(searchTerm.toLowerCase())).sort((a,b) => b.billedAmount - a.billedAmount);
    }, [projects, searchTerm]);

    // Nicht mehr erreichbar: gebucht wird ausschliesslich ueber eine Rechnung.
    // Bewusst NICHT geloescht, aber auch nicht mehr aufrufbar — der Dialog
    // dahinter schreibt quantityBilled ohne Beleg, und genau diesen zweiten
    // Schreibpfad soll es nicht mehr geben. Entfernt wird er, sobald der
    // Altbestand ueber das Rechnungsmodul nachgezogen ist.
    const _legacyOpenBillingModal = () => {
        const values: Record<string, number> = {};
        getNormalizedServices(selectedProjectForAccounting).forEach((s: ServiceItem) => {
            // Ueber openQuantity statt used - billed: eine gedeckte Position
            // (Monatspauschale) und eine verzichtete Menge duerfen auch hier
            // nie als buchbar vorbelegt werden.
            if (selectedServices.has(s.id)) values[s.id] = openQuantity(s);
        });
        setBillingValues(values); setBillingDate(getLocalDateStr()); setBillingModalOpen(true);
    };

    const handleOpenHistory = async () => {
        if (!selectedProjectForAccounting) return;
        setHistoryHinweis(null);
        const currentProjectId = selectedProjectForAccounting.id;
        const currentMeta = selectedProjectForAccounting.data?.metadata as ReportMetadata;
        let projectIdsToFetch = [currentProjectId];
        // Die Toechter aus der geladenen Liste: ihr Config ist aus den Spalten
        // (Nr. 71) zusammengesetzt. Eine Rolle 'parent' hat nie ein Pfad
        // geschrieben — die alte Bedingung darauf war immer falsch und liess
        // die Archive der Toechter weg.
        projects.filter(p => { const m = p.data?.metadata as ReportMetadata; return m?.globalData?.linkedProjectConfig?.role === 'child' && m?.globalData?.linkedProjectConfig?.linkedProjectId === currentProjectId; }).forEach(child => projectIdsToFetch.push(child.id));
        let allHistory: any[] = [];
        // Im Lesemodus ohne die Selbstheilung (Base64 -> URL): sie schreibt
        // ins Archiv und in den Speicher, und hier wird nur angesehen.
        (await Promise.all(projectIdsToFetch.map(pid => fetchReportHistory(pid, { skipMigration: nurLesen })))).forEach(h => { allHistory = [...allHistory, ...h]; });
        // Der noch nicht archivierte Bericht als eigener Eintrag — aus dem
        // VOLL geladenen Projekt. selectedProjectForAccounting ist ein
        // Listeneintrag (nur `metadata`): sein PDF kam ohne Maengel und ohne
        // Firmendaten. Geladen wird nur, wenn der Eintrag gebraucht wird, und
        // IMMER ohne Selbstheilung (archivLiveEintrag.ts): der Eintrag dient
        // nur der Ansicht und dem PDF, und das Oeffnen des Archivs soll am
        // laufenden Projekt nichts schreiben — Base64-Bilder zeigt das PDF
        // ebenso an. Ebenso OHNE Anker: ist das Projekt im Editor offen
        // (Sprung Projekt → Buchhaltung schliesst es nicht), bleibt dessen
        // Ausgangsstand fuer die Konfliktpruefung stehen.
        if (brauchtLiveArchivEintrag(allHistory, currentMeta)) {
            try {
                const fullProject = await loadProjectById(currentProjectId, undefined, { skipMigration: true, ohneAnker: true });
                if (!fullProject) throw new Error("Projekt nicht gefunden");
                const liveEintrag = liveArchivEintrag(allHistory, fullProject.data);
                if (liveEintrag) allHistory.push(liveEintrag);
            } catch (e) {
                // Die Archivzeilen sind da und bleiben ansehbar — nur der
                // aktuelle Stand fehlt, und das soll man nicht erst am
                // fehlenden Eintrag merken. Hinweis im Archiv-Dialog, kein
                // Browser-Dialog (CI-Ratsche).
                console.error(e);
                setHistoryHinweis('Der aktuelle Stand des Projekts konnte nicht geladen werden. Angezeigt werden nur die archivierten Berichte.');
            }
        }
        allHistory.sort((a, b) => { const nc = (a.projectName || '').localeCompare(b.projectName || ''); return nc !== 0 ? nc : b.inspectionNumber - a.inspectionNumber; });
        setProjectHistory(allHistory); setHistoryModalOpen(true);
    };
    
    const handleExecuteBilling = async () => {
          if (!selectedProjectForAccounting || selectedServices.size === 0 || nurLesen) return;
          try {
              const fullProject = await loadProjectById(selectedProjectForAccounting.id);
              if (!fullProject) throw new Error("Projekt nicht gefunden");
              const meta = fullProject.data.metadata as ReportMetadata;
              const services = meta.globalData?.services;
              if (!services) return;
              let totalBilledNow = 0;
              const dateStr = new Date(billingDate).toLocaleDateString('de-DE');
              let newMetadata = { ...meta };

              // Zweitschrift ins Buchungsjournal (S4b): der Stand VOR dem
              // Vorgang und die neuen Zeilen, getrennt festgehalten.
              // newMetadata teilt sich globalData mit meta — der Verweis auf
              // das ALTE Array muss deshalb hier stehen, vor der ersten
              // Zuweisung unten.
              const journalVorherLog: ServiceTransaction[] = meta.globalData?.serviceLog || [];
              const journalNeueZeilen: ServiceTransaction[] = [];
              
              const currentYear = new Date().getFullYear();
              const bookingRef = `BU-${currentYear}-${Math.floor(Math.random() * 100000).toString().padStart(5, '0')}`;
              
              let updatedServices = services.map((s: ServiceItem) => {
                  if (selectedServices.has(s.id)) {
                      // Hard cap: never bill more than what is open (used - alreadyBilled).
                      // The modal's `max` attribute alone does not prevent typed overbooking.
                      const maxBillable = Math.max(0, (s.quantityUsed || 0) - (s.quantityBilled || 0));
                      const amountToBill = Math.min(Math.max(0, billingValues[s.id] || 0), maxBillable);
                      if (amountToBill > 0) {
                          totalBilledNow += amountToBill * s.unitPrice;
                          const newBilled = (s.quantityBilled || 0) + amountToBill;
                          const newTransaction = { 
                              id: crypto.randomUUID(), 
                              date: new Date(billingDate).toISOString(), // Service Date (selected in modal)
                              serviceId: s.id, 
                              quantity: amountToBill, 
                              performerId: currentUser?.id || 'admin', 
                              performerName: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin', 
                              isSubstitution: false, 
                              description: `Manuelle Verbuchung durch Admin`, 
                              bookedAt: new Date().toISOString(), // Transaction Date (now)
                              bookedBy: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin', 
                              bookedById: currentUser?.id, 
                              bookingReference: bookingRef 
                          };
                          if (!newMetadata.globalData) newMetadata.globalData = { status: 'active' };
                          newMetadata.globalData.serviceLog = [newTransaction, ...(newMetadata.globalData.serviceLog || [])];
                          journalNeueZeilen.push(newTransaction);
                          return { ...s, quantityBilled: newBilled, status: newBilled >= s.quantityUsed ? 'billed' as const : s.status };
                      }
                  }
                  return s;
              });
              if (newMetadata.globalData?.serviceLog) {
                  // Ungebuchte Zeilen der Positionen als abgerechnet stempeln.
                  // Eine vorhandene Referenz (z. B. `ALT-…`) bleibt samt
                  // Buchungsart stehen; nur Zeilen ohne Referenz bekommen
                  // 'LEGACY-AUTO' (serviceLogUtils.stampLegacyEntry).
                  newMetadata.globalData.serviceLog = stampLegacyEntries(newMetadata.globalData.serviceLog, selectedServices);
              }
              const newNote = { id: crypto.randomUUID(), text: `Abrechnung erstellt (Buchungsdatum: ${dateStr}). Betrag: ${formatCurrency(totalBilledNow)}`, date: new Date().toISOString(), author: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'System' };
              newMetadata.globalData = { ...meta.globalData, services: updatedServices, notes: [newNote, ...(meta.globalData?.notes || [])] };
              await saveProjectToCloud({ ...fullProject.data, metadata: newMetadata }, fullProject.id);
              // JSON zuerst, Journal danach — nur die neuen Buchungszeilen.
              // Das nachtraegliche Stempeln der Alt-Eintraege oben aendert
              // BESTEHENDE Zeilen; das Journal kennt kein Update und bekommt
              // es deshalb nicht. Seit der Stempel eine vorhandene Referenz
              // stehen laesst, weichen dort nur noch `bookedAt`/`bookedBy` ab
              // (bei Zeilen ohne Referenz zusaetzlich 'LEGACY-AUTO').
              // Eigener catch: der umgebende zeigt "Fehler beim Speichern" —
              // die Zweitschrift darf nie so aussehen, als waere die Buchung
              // fehlgeschlagen.
              try {
                  await mirrorEntriesToJournal({
                      projectId: fullProject.id,
                      entries: journalNeueZeilen,
                      previousLog: journalVorherLog,
                      source: 'admin_accounting',
                      linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
                  });
              } catch (je) {
                  console.error('[serviceJournal] Zweitschrift der Verbuchung fehlgeschlagen', je);
              }
              onRefresh(); setSelectedProjectForAccounting(null); setSelectedServices(new Set()); setBillingModalOpen(false);
              alert(`Erfolgreich gebucht! ${formatCurrency(totalBilledNow)}`);
          } catch (e: any) { alert("Fehler beim Speichern: " + e.message); }
    };
    
    const handleRestorePosition = async (projectId: string, serviceId: string) => {
        if (!darf.altbuchungStornieren.erlaubt) return;
        if (!confirm("Möchten Sie diese Abrechnung wirklich stornieren?")) return;
        try {
            const fullProject = await loadProjectById(projectId);
            if (!fullProject) return;
            const meta = fullProject.data.metadata;
            const services = meta.globalData?.services || [];
            const targetService = services.find((s:any) => s.id === serviceId);
            if (!targetService) return;

            // Storno-Hygiene: zurueckgenommen wird genau, was die Verbuchung
            // geschrieben hat, OHNE die Leistungshistorie zu zerstoeren.
            // - Ihre Buchungszeilen werden entfernt: "BU-…" und die Vorgaengerform
            //   "MANUAL-…" mit dem Text „Manuelle Verbuchung".
            // - Ihr Stempel auf den ungebuchten Zeilen wird umgekehrt
            //   ('LEGACY-AUTO' faellt samt Buchungsfeldern, bei erhaltener
            //   Referenz nur `bookedAt`/`bookedBy`).
            // - Zeilen anderer Vorgaenge (ALT-, MB-, VZ-, RN-, FLAT-, UW-,
            //   MANUAL- ohne Verbuchungstext, Berichtsbuchung) bleiben
            //   unveraendert.
            // Rein und getestet: serviceLogUtils.planPositionRestore. Die ids
            // der WIRKLICH entfernten Zeilen — nur sie werden im Journal
            // zurueckgenommen (unten, nach dem Speichern).
            const { log: updatedLog, removedIds: entfernteZeilenIds, removedQuantity } = planPositionRestore(
                meta.globalData?.serviceLog || [],
                serviceId,
            );
            // Der Zaehler faellt um die Verbuchung; was stehen bleibende MB-/
            // ALT-Zeilen belegen, bleibt abgerechnet (sonst hielte Riegel
            // Nr. 111 ihn fest bzw. die Alt-Menge stuende wieder offen).
            const billedBefore = Number(targetService.quantityBilled) || 0;
            const billedAfter = billedAfterPositionRestore(billedBefore, updatedLog, removedQuantity, serviceId);
            const updatedServices = services.map((s: any) => s.id === serviceId
                ? { ...s, quantityBilled: billedAfter, status: billedAfter > 0 && billedAfter >= (s.quantityUsed || 0) ? 'billed' : 'open' }
                : s);

            const rest = billedAfter > 0 ? `; weiter abgerechnet (manuelle Verbuchung/Alt-Rechnung): ${billedAfter} ${targetService.unit}` : '';
            const newNote = { id: crypto.randomUUID(), text: `Abrechnung storniert für Leistung: ${targetService.title} (${Math.round((billedBefore - billedAfter) * 1000) / 1000} ${targetService.unit}${rest})`, date: new Date().toISOString(), author: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin' };

            // ─── Die 'BU-'-Zeilen serverseitig entfernen — VOR dem Speichern ──
            //
            // Ab Migration Nr. 66 darf eine BU-/ALT-Zeile das Protokoll nur
            // noch ueber `service_log_remove_entries` verlassen; der Riegel
            // `service_log_guard_trg` wirft sonst, weil er sie in OLD sieht und
            // in NEW vermisst. Nach diesem Aufruf fehlen sie bereits in OLD,
            // und der Save unten schreibt nur noch den Rest (zurueckgesetzte
            // Menge, ent-buchte Zeilen, Notiz).
            //
            // Der lokale Stand passt dazu: `updatedLog` ist genau der
            // Reststand, der hier gespeichert wird — er enthaelt die entfernten
            // Zeilen nicht mehr und kann sie deshalb nicht wieder anlegen.
            //
            // `legacy` (RPC noch nicht ausgerollt) laeuft weiter wie bisher:
            // der Save nimmt die Zeilen mit. Ein echter Fehler wirft und wird
            // unten gemeldet — still weiterlaufen darf der Storno nicht.
            await removeServiceLogEntries(
                fullProject.id,
                entfernteZeilenIds,
                `Storno der Abrechnung: ${targetService.title}`,
            );

            await saveProjectToCloud({ ...fullProject.data, metadata: { ...meta, globalData: { ...meta.globalData, services: updatedServices, serviceLog: updatedLog, notes: [newNote, ...(meta.globalData?.notes || [])] } } }, fullProject.id);
            // Ruecknahme im Buchungsjournal (S4c) — nach dem bestaetigten
            // Speichern, und nur fuer die ENTFERNTEN 'BU-'-Zeilen.
            //
            // Bewusst NICHT zurueckgenommen: die uebrigen Zeilen bleiben im
            // Protokoll stehen. Unveraenderte Zeilen stimmen mit dem Journal
            // ohnehin ueberein; die ent-stempelten verlieren nur, was der
            // Stempel hinzugefuegt hatte — und den hat das Journal nie
            // bekommen (Verbuchung spiegelt nur ihre neuen Zeilen). Sie zu
            // voiden naehme eine Leistung zurueck, die es weiterhin gibt.
            //
            // Eigener catch: der umgebende zeigt "Fehler beim
            // Wiederherstellen" — das Storno ist hier laengst geschrieben.
            try {
                await voidJournalEntries(fullProject.id, entfernteZeilenIds, 'storno');
            } catch (je) {
                console.error('[serviceJournal] Ruecknahme der Verbuchung fehlgeschlagen', je);
            }
            onRefresh(); alert("Position erfolgreich wiederhergestellt.");
        } catch (e) {
            // Der Grund gehoert in die Meldung. Frueher stand hier ein blosses
            // "Fehler beim Wiederherstellen." — mit der serverseitigen
            // Entfernung (Nr. 66) kann der Vorgang jetzt an einer Ursache
            // scheitern, die man dem Anwender nennen muss, statt ihn raten zu
            // lassen, ob der Storno gelaufen ist. Diese Ansicht spricht
            // durchgehend ueber confirm/alert; ein eigener Dialog nur fuer
            // diesen Fall waere ein zweiter Weg fuer dieselbe Auskunft.
            alert("Fehler beim Wiederherstellen: " + describeError(e));
        }
    };
    
    /**
     * VERZICHT — die Buchhaltung schliesst eine erbrachte, aber nie
     * abgerechnete Menge.
     *
     * Der wiederkehrende Fall: das Projekt ist schlussgerechnet, eine Begehung
     * steht trotzdem weiter als offen. Bis hierher liess sie sich nur per SQL
     * aus dem Weg raeumen (supabase/fix_5983_…, fix_6211_…).
     *
     * Bewusst KEIN eigener Schreibpfad in dieser Datei: die Regeln stehen in
     * serviceWaiverLogic, das Schreiben in serviceWaiverService. Diese Ansicht
     * hat schon einmal zwei Wege auf dieselben Mengen gehabt, und der zweite
     * ist bis heute stillgelegt (_legacyOpenBillingModal).
     */
    const handleWaive = async (quantity: number, reason: string, entryIds?: string[]) => {
        if (!selectedProjectForAccounting || !waiverServiceId) {
            return { ok: false, message: 'Keine Position gewählt.' };
        }
        const result = await waiveServiceQuantity({
            projectId: selectedProjectForAccounting.id,
            serviceId: waiverServiceId,
            quantity,
            entryIds,
            reason,
            user: currentUser,
        });
        // Erst nach dem Schliessen des Projekts neu laden: onRefresh ersetzt die
        // Projektliste, und die Detailansicht haengt an einem Abzug daraus.
        if (result.ok) { setSelectedProjectForAccounting(null); onRefresh(); }
        return result;
    };

    const handleReleaseWaiver = async (reason: string) => {
        if (!selectedProjectForAccounting || !waiverServiceId) {
            return { ok: false, message: 'Keine Position gewählt.' };
        }
        const result = await releaseServiceWaiver({
            projectId: selectedProjectForAccounting.id,
            serviceId: waiverServiceId,
            reason,
            user: currentUser,
        });
        if (result.ok) { setSelectedProjectForAccounting(null); onRefresh(); }
        return result;
    };

    /**
     * RUECKNAHME — die Buchung haette es nie geben duerfen.
     *
     * Anders als der Verzicht senkt sie `quantityUsed`. Welcher der beiden
     * Vorgaenge richtig ist, entscheidet sich daran, ob die Leistung erbracht
     * wurde; die Maske sagt das in beiden Dialogen im Klartext.
     */
    const handleReverse = async (target: ReversalTarget, reason: string) => {
        if (!selectedProjectForAccounting || !reversalServiceId) {
            return { ok: false, message: 'Keine Position gewählt.' };
        }
        const result = await reverseServiceBooking({
            projectId: selectedProjectForAccounting.id,
            serviceId: reversalServiceId,
            target,
            reason,
            user: currentUser,
        });
        if (result.ok) { setSelectedProjectForAccounting(null); onRefresh(); }
        return result;
    };

    /**
     * MANUELLE VERBUCHUNG — eine in der Vergangenheit abgerechnete Menge
     * stempeln, obwohl der Beleg dazu fehlt.
     *
     * Der Ausweg fuer Bestandsprojekte, deren Rechnungen im alten System
     * liegen: wer das PDF hat, liest es ein (dann haengt ein Beleg daran und
     * der Betrag wird als Vorrechnung abgezogen) — wer es nicht mehr hat,
     * konnte den Stand bisher nur per SQL geradeziehen.
     *
     * Bewusst KEIN eigener Schreibpfad in dieser Datei: die Regeln stehen in
     * manualBillingLogic, das Schreiben in manualBillingService. Der alte
     * zweite Weg auf dieselben Mengen (_legacyOpenBillingModal) bleibt
     * stillgelegt — er kannte weder Datum noch Herkunft noch Ruecknahme.
     */
    const handleManualBilling = async (payload: {
        quantity: number;
        billedOn: string;
        invoiceNumber: string;
        reason: string;
        customerId: string;
        splitPartyId: string;
    }) => {
        if (!selectedProjectForAccounting || !manualBillingServiceId) {
            return { ok: false, message: 'Keine Position gewählt.' };
        }
        const result = await bookManualBilling({
            projectId: selectedProjectForAccounting.id,
            serviceId: manualBillingServiceId,
            quantity: payload.quantity,
            billedOn: payload.billedOn,
            invoiceNumber: payload.invoiceNumber,
            reason: payload.reason,
            customerId: payload.customerId,
            splitPartyId: payload.splitPartyId,
            // null (Schluessel unbekannt) sperrt bereits im Dialog; hier zaehlt
            // nur, ob ueberhaupt aufgeteilt wird.
            splitProject: (splitParticipants?.length || 0) > 1,
            user: currentUser,
        });
        if (result.ok) { setSelectedProjectForAccounting(null); onRefresh(); }
        return result;
    };

    const handleUndoManualBilling = async (entryId: string, reason: string) => {
        if (!selectedProjectForAccounting || !manualBillingServiceId) {
            return { ok: false, message: 'Keine Position gewählt.' };
        }
        const result = await undoManualBilling({
            projectId: selectedProjectForAccounting.id,
            serviceId: manualBillingServiceId,
            targetEntryId: entryId,
            reason,
            user: currentUser,
        });
        if (result.ok) { setSelectedProjectForAccounting(null); onRefresh(); }
        return result;
    };

    // NEW: Open PerformanceProofEditor instead of old modal
    const handleExportPerformance = async () => {
        if (!selectedProjectForAccounting) return;
        try {
            // Lesemodus ohne Selbstheilung. Anders als beim Archiv MIT Anker:
            // aus dem Leistungsnachweis-Editor wird gespeichert.
            const fullProject = await loadProjectById(selectedProjectForAccounting.id, undefined, { skipMigration: nurLesen });
            if (!fullProject) throw new Error("Projekt nicht gefunden");
            setProofEditorProject(fullProject);
        } catch (e) {
            console.error(e);
            alert("Fehler beim Laden des Projekts");
        }
    };

    const currentViewServices = useMemo(() => getNormalizedServices(selectedProjectForAccounting), [selectedProjectForAccounting]);

    // MONATSPAUSCHALE: der ruhige Hinweis je gedeckter Position — dieselbe
    // Formulierung wie auf dem Leistungsnachweis, mit dem Namen der deckenden
    // Pauschale. Ohne ihn staende in "Offen (Abrechenbar)" ein Strich, den
    // niemand von einem Rechenfehler unterscheiden koennte.
    const currentViewFlatHints = useMemo(() => flatCoverageHints(currentViewServices), [currentViewServices]);

    // Der Schluessel des gewaehlten Projekts. Ohne Projekt leer — und leer
    // heisst "ungeteilt", nicht "noch nicht geladen": der Einlese-Dialog
    // verlangt die Empfaengerangabe erst ab zwei Beteiligten, und ein
    // Zwischenzustand duerfte ihn nicht faelschlich freigeben. Deshalb wird
    // erst gesetzt, wenn beide Abfragen zurueck sind.
    useEffect(() => {
        let abgebrochen = false;
        const projectId = selectedProjectForAccounting?.id;
        if (!projectId) {
            setSplitParticipants([]);
            return;
        }
        // null heisst UNBEKANNT und nicht "ungeteilt". Der Unterschied ist
        // tragend: waehrend des Ladens und nach einem Fehlschlag darf ein
        // Altbeleg nicht ohne Auftraggeber hereinkommen — er waere sonst
        // Altbestand ohne Zuordnung und sperrte spaeter jede Teilrechnung, an
        // einer Stelle, die niemand mehr mit diesem Import in Verbindung
        // bringt.
        setSplitParticipants(null);
        void (async () => {
            try {
                const split = await fetchProjectSplit(projectId);
                if (abgebrochen) return;
                if (!split || split.participants.length === 0) {
                    setSplitParticipants([]);
                    setSplitLegacy(new Map());
                    return;
                }
                setSplitLegacy(legacyPartyByCustomer(split));
                const customers = await fetchCustomers();
                if (abgebrochen) return;
                const nameById = new Map(customers.map((c) => [c.id, c.name]));
                setSplitParticipants(
                    split.participants.map((teilnehmer) => ({
                        // Die Identitaet ist der TEILNEHMER und nicht der
                        // Auftraggeber: derselbe Auftraggeber kann mehrere
                        // Abschnitte haben, und die Buchung muss auf den
                        // Abschnitt zeigen (projectSplit, SQL Nr. 54).
                        partyId: teilnehmer.partyId,
                        customerId: teilnehmer.customerId,
                        // Ohne Stammsatz die Kennung zeigen statt einen leeren
                        // Eintrag: unbenannt waere die Auswahl unbedienbar. Der
                        // Abschnitt kommt dazu, sonst staenden zwei Teilnehmer
                        // desselben Auftraggebers ununterscheidbar nebeneinander.
                        name:
                            (nameById.get(teilnehmer.customerId) ?? teilnehmer.customerId) +
                            (teilnehmer.label ? ` — ${teilnehmer.label}` : ''),
                    })),
                );
            } catch {
                // BEWUSST auf null und nicht auf []: ein Fehlschlag ist keine
                // Auskunft. Die Abrechnung bleibt bedienbar, nur das Einlesen
                // von Alt-Rechnungen sperrt, bis der Schluessel sicher
                // feststeht.
                if (!abgebrochen) setSplitParticipants(null);
            }
        })();
        return () => {
            abgebrochen = true;
        };
    }, [selectedProjectForAccounting?.id, splitEditorOpen]);

    /**
     * Belegnummern, deren Alt-Positionen bereits per Backfill uebernommen
     * wurden (bookingReference 'ALT-<Nummer>' im serviceLog des Projekts).
     *
     * Grundlage fuer die Idempotenz-Warnung im Import-Dialog: ein zweiter
     * Import derselben Alt-Rechnung soll nicht folgenlos neue Positionen
     * vorauswaehlen, nur weil das OCR-Titel-Matching sie (noch) nicht als
     * "vorhanden" erkennt.
     */
    const usedBackfillReferences = useMemo(() => {
        const meta = selectedProjectForAccounting?.data?.metadata as ReportMetadata | undefined;
        const log = meta?.globalData?.serviceLog || [];
        return new Set(
            log
                .map((t) => t.bookingReference)
                .filter((ref): ref is string => Boolean(ref) && ref.startsWith(BACKFILL_REFERENCE_PREFIX)),
        );
    }, [selectedProjectForAccounting]);

    /**
     * Mengen, die eine Alt-Rechnung bereits abgegolten hat (ALT-Buchungen im
     * Leistungsprotokoll), je Leistung.
     *
     * quantityBilled bleibt bei nachgetragenen Mengen bewusst 0 — abgerechnet
     * wird der Gesamtstand von der kumulativen Rechnung, deren Abzug die
     * Alt-Abschlaege enthaelt. Fuer die ANZEIGE ist das irrefuehrend: die Menge
     * stuende hier als "Offen (Abrechenbar)", obwohl sie mit dem Altbeleg
     * laengst gefordert wurde. Diese Spalte rechnet sie deshalb heraus und
     * benennt die Herkunft; gerechnet wird an der Rechnung selbst nichts anders.
     */
    const altCoverage = useMemo(() => {
        const meta = selectedProjectForAccounting?.data?.metadata as ReportMetadata | undefined;
        return altCoverageByService(meta?.globalData?.serviceLog || []);
    }, [selectedProjectForAccounting]);

    /**
     * Von Hand als abgerechnet gebuchte Menge je Leistung ('MB-'-Zeilen,
     * Ruecknahmen verrechnet).
     *
     * Nur fuer die ANZEIGE und fuer die Frage, ob der Vorgang an dieser Zeile
     * ueberhaupt angeboten wird: gerechnet wird mit `quantityBilled`, in das
     * die Menge bereits eingegangen ist. Sie hier ein zweites Mal abzuziehen
     * waere die Doppelzaehlung, gegen die die Alt-Deckung daneben ausdruecklich
     * `effectiveAlt` bildet.
     */
    const manualBilled = useMemo(() => {
        const meta = selectedProjectForAccounting?.data?.metadata as ReportMetadata | undefined;
        const log = meta?.globalData?.serviceLog || [];
        const out = new Map<string, number>();
        for (const s of getNormalizedServices(selectedProjectForAccounting)) {
            const menge = manuallyBilledQuantity(log, s.id);
            if (menge > 0) out.set(s.id, menge);
        }
        return out;
    }, [selectedProjectForAccounting]);

    // Nachtraegliche Korrektur einer Alt-Zuordnung: Quelle (Zeile mit
    // ALT-Buchung) und gewaehltes Ziel.
    const [correctionServiceId, setCorrectionServiceId] = useState<string | null>(null);
    const [correctionTargetId, setCorrectionTargetId] = useState<string>('');

    /**
     * Alt-Positionen aus "Letzte Rechnung einlesen" ins Projekt uebernehmen.
     *
     * Liegt hier und nicht im Panel, weil nur der Host den bestehenden
     * Speicherpfad hat: loadProjectById → saveProjectToCloud. Frisch laden und
     * den vollstaendigen Stand zurueckschreiben — ein eigener Direkt-Write auf
     * einzelne Spalten wuerde am JSON-Blob vorbeischreiben.
     *
     * Der lokale Projektstand wird danach mitgezogen, damit ein zweiter Import
     * derselben Datei die Positionen als "vorhanden" erkennt statt sie erneut
     * anzulegen (onRefresh erneuert nur die Liste, nicht dieses Objekt).
     */
    const handleBackfillPositions = async ({
        selections,
        serviceDate,
        invoiceNumber,
        customerId,
        partyId,
    }: {
        selections: BackfillSelection[];
        serviceDate: string;
        invoiceNumber: string;
        /**
         * Der Teilnehmer der Aufteilung, dem der Altbeleg gehoert. Feiner als
         * `customerId` und seit den Abschnitten die massgebliche Angabe: bei
         * mehreren Haeusern desselben Auftraggebers waere die Menge sonst
         * zwischen ihnen nicht zu entscheiden.
         */
        partyId?: string;
        /**
         * Auftraggeber des Altbelegs. Bei einem geteilten Projekt deckt der
         * Beleg nur DESSEN Anteil ab; ohne den Stempel zaehlte die Menge als
         * Altbestand ohne Zuordnung und sperrte jede weitere Teilrechnung.
         */
        customerId?: string;
    }) => {
        if (!selectedProjectForAccounting || selections.length === 0) return;
        const projectId = selectedProjectForAccounting.id;
        const fullProject = await loadProjectById(projectId);
        if (!fullProject) throw new Error('Projekt nicht mehr verfügbar.');
        const meta = fullProject.data.metadata as ReportMetadata;
        const globalData = meta.globalData || { status: 'active' as const };
        const performerName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin';

        // GANZ oder gar nicht: zwischen Abgleich und Uebernahme wurde das
        // Projekt neu geladen. Fehlt eine Zielposition, ginge ihre
        // Protokollzeile auf eine serviceId ins Leere — die Menge waere gebucht,
        // aber nirgends sichtbar, und die verbrauchte ALT-Referenz liesse den
        // zweiten Anlauf gewarnt starten. Vor dem ersten Schreibzugriff.
        const missingTargets = missingBackfillTargets(selections, globalData.services || []);
        if (missingTargets.length) {
            throw new Error('Die Zielposition ist nicht mehr im Projekt — bitte die Ansicht neu laden.');
        }

        const { services, log } = buildBackfillServices(selections, {
            serviceDate,
            invoiceNumber,
            performerId: currentUser?.id || '',
            performerName,
            newId: () => crypto.randomUUID(),
            customerId,
            partyId,
        });
        // Zugeordnete Zeilen legen KEINE Position an, sondern erhoehen die
        // erledigte Menge der bestehenden — genau das verhindert die Dublette,
        // die sonst als zweite "offene" Leistung im Abgleich stuende.
        const increments = backfillQuantityIncrements(selections);

        // ERSETZEN, nicht daneben schreiben: derselbe Altbeleg ein zweites Mal
        // eingelesen nimmt zuerst zurueck, was sein erster Lauf geschrieben hat.
        // Ohne diesen Schritt stehen beide Laeufe im Protokoll und die Menge
        // zaehlt doppelt — der Weg, auf dem aus 18 Stunden 36 wurden.
        //
        // Die Ziele der aktuellen Auswahl sind geschuetzt: sie werden gleich
        // wieder bebucht und duerfen zwischendurch nicht wegfallen.
        const reverted = revertBackfill(
            globalData.services || [],
            globalData.serviceLog || [],
            invoiceNumber,
            new Set(increments.keys()),
            customerId,
        );

        // Bei geteiltem Projekt gilt die Stand-Regel JE AUFTRAGGEBER: drei
        // Belege ueber je ein Drittel summieren sich dann auf den vollen Stand,
        // waehrend derselbe Beleg zweimal eingelesen weiterhin nichts bewegt.
        // Gerechnet wird gegen das BEREINIGTE Protokoll — mit dem Stand vor der
        // Ruecknahme zaehlte der eigene erste Lauf als fremder Stand.
        const splitContext =
            customerId && partyId
                ? {
                      partyId,
                      customerId,
                      legacy: splitLegacy,
                      serviceLog: reverted.serviceLog,
                  }
                : undefined;

        // Die Wirkung wird gegen den BEREINIGTEN Stand gerechnet (nach
        // revertBackfill) und in die ALT-Zeilen gestempelt. Nur so ist die
        // spaetere Ruecknahme die exakte Umkehrung: seit die Zaehler angehoben
        // statt addiert werden, ist die Belegmenge nicht mehr das Mass dessen,
        // was tatsaechlich geschrieben wurde.
        // Leistung NACH dem Leistungsdatum steckt nicht im Altbeleg und kommt
        // auf den neuen Abrechnungsstand oben drauf (5284: 7 Begehungen nach
        // der letzten Alt-Rechnung). Beide Aufrufe bekommen denselben Bezug,
        // sonst waere die Ruecknahme nicht mehr die Umkehrung.
        const zeit = { serviceLog: reverted.serviceLog, serviceDate };
        const effective = backfillEffectiveIncrements(reverted.services, increments, splitContext, zeit);
        const stampedLog = stampAppliedIncrements(log, effective);

        const mergedServices = [
            ...applyBackfillIncrements(reverted.services, increments, splitContext, zeit),
            ...services,
        ];
        const noteParts = [
            reverted.revertedByService.size
                ? `${reverted.revertedByService.size} Position(en) aus dem vorherigen Einlesen ersetzt`
                : '',
            services.length
                ? `${services.length} Position(en) neu angelegt (beauftragt = erledigt)`
                : '',
            increments.size
                ? `${increments.size} bestehende(n) Position(en) Menge zugebucht`
                : '',
        ].filter(Boolean);
        const note = {
            id: crypto.randomUUID(),
            text: `Alt-Rechnung ${invoiceNumber || '(ohne Nummer)'} nachgetragen: ${noteParts.join(', ')} (Leistungsdatum ${serviceDate}).`,
            date: new Date().toISOString(),
            author: performerName,
        };
        const finalData = {
            ...fullProject.data,
            metadata: {
                ...meta,
                globalData: {
                    ...globalData,
                    services: mergedServices,
                    // reverted.serviceLog, nicht globalData.serviceLog: die
                    // Zeilen des vorherigen Laufs sind hier bereits heraus.
                    serviceLog: [...stampedLog, ...reverted.serviceLog],
                    notes: [note, ...(globalData.notes || [])],
                },
            },
        };

        // ─── Die ALT-Zeilen des vorherigen Laufs serverseitig entfernen ──────
        //
        // ERST DIE RPC, DANN DER SAVE — und die RPC so spaet wie moeglich,
        // unmittelbar vor dem Speichern: alles darueber ist reine Rechnung und
        // darf noch abbrechen, ohne dass am Server schon etwas fehlt.
        //
        // Ab Migration Nr. 66 darf eine BU-/ALT-Zeile das Protokoll nur noch
        // ueber `service_log_remove_entries` verlassen. Speicherte diese Stelle
        // zuerst den Blob ohne sie, saehe der Riegel `service_log_guard_trg`
        // sie in OLD stehen und in NEW fehlen — und wuerfe; die ganze
        // Uebernahme scheiterte. Nach der RPC fehlen sie bereits in OLD.
        //
        // Der lokale Stand passt danach zum Serverstand: `finalData` fuehrt
        // `reverted.serviceLog` (die entfernten Zeilen sind dort heraus) plus
        // die NEUEN Zeilen dieses Laufs mit frischen uuids. Der Save legt also
        // keine gerade entfernte Zeile wieder an — was der Riegel als
        // Neuanlage durchwinken wuerde und die Altmenge doppelt im Protokoll
        // stehen liesse.
        //
        // Kein try/catch: ein echter Fehler muss die Uebernahme abbrechen und
        // wird vom aufrufenden Panel im Dialog angezeigt (ExternalAdvancesPanel
        // faengt den Wurf mit `setError(describeError(e))`). Ist die RPC nicht
        // ausgerollt (`legacy`), laeuft alles wie bisher ueber den Blob-Save.
        await removeServiceLogEntries(
            projectId,
            reverted.removedEntryIds,
            `Alt-Rechnung ${invoiceNumber || '(ohne Nummer)'} erneut eingelesen: vorheriger Nachtrag ersetzt`,
        );

        await saveProjectToCloud(finalData, fullProject.id);

        // Ruecknahme im Buchungsjournal (S4c) — VOR der Zweitschrift und nur
        // nach dem bestaetigten Speichern.
        //
        // Und ausdruecklich NACH der serverseitigen Entfernung oben: das
        // Journal kennt kein Un-void. Liefe es zuerst und die Entfernung
        // scheiterte, waere die Journalzeile unwiderruflich stillgelegt,
        // waehrend das JSON die Menge weiterfuehrt. So bleibt im schlimmsten
        // Fall eine ueberzaehlige Journalzeile stehen — die meldet die
        // Abweichungssicht, und ein erneuter Aufruf holt sie nach.
        //
        // revertBackfill oben hat die ALT-Zeilen des VORHERIGEN Laufs aus dem
        // JSON entfernt, und buildBackfillServices legt inhaltsgleiche Zeilen
        // mit frischen uuids wieder an. Das Journal kennt kein Delete: ohne
        // diesen Schritt stuenden danach 2n Journalzeilen fuer n JSON-Zeilen,
        // und weil die neuen ids freie uuids sind, greift auch der
        // inhaltsabgeleitete Dublettenschluessel nicht — die Altmenge zaehlte
        // doppelt. Genau der Weg, auf dem aus 18 Stunden 36 wurden.
        //
        // Geschickt werden nur die tatsaechlich entfernten Zeilen; welche das
        // waren, weiss revertBackfill (removedEntryIds), nicht diese Stelle.
        //
        // Eigener catch: der Aufrufer meldet einen Fehlschlag als
        // fehlgeschlagene Uebernahme, und die ist hier laengst geschrieben.
        //
        // Zur REIHENFOLGE (erst zuruecknehmen, dann anhaengen): sie ist eine
        // Lesbarkeitsfrage und keine Absicherung. Beide Schritte treffen
        // verschiedene Zeilen, weil die neuen ihre eigenen frischen uuids
        // tragen. Traefen sie je dieselbe id — moeglich nur, wenn eine neue
        // Zeile ohne id ins JSON kaeme und deshalb einen inhaltsabgeleiteten
        // Schluessel bekaeme —, ginge es in BEIDEN Reihenfolgen schief; das
        // ist die dokumentierte Grenze im Kopf von serviceJournal.ts.
        try {
            await voidJournalEntries(projectId, reverted.removedEntryIds, 'backfill_revert');
        } catch (je) {
            console.error('[serviceJournal] Ruecknahme des vorherigen Alt-Nachtrags fehlgeschlagen', je);
        }

        // Zweitschrift ins Buchungsjournal (S4b) — JSON zuerst, Journal
        // danach. Angehaengt werden nur die ALT-Zeilen dieses Laufs
        // (stampedLog).
        //
        // previousLog ist der Stand VOR dem ganzen Vorgang, nicht der
        // bereinigte: die ids der zurueckgenommenen Zeilen sind im Journal
        // weiterhin belegt.
        //
        // Eigener catch: siehe oben.
        try {
            await mirrorEntriesToJournal({
                projectId,
                entries: stampedLog,
                previousLog: globalData.serviceLog || [],
                source: 'alt_import',
                linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
            });
        } catch (je) {
            console.error('[serviceJournal] Zweitschrift des Alt-Nachtrags fehlgeschlagen', je);
        }

        setSelectedProjectForAccounting((prev) =>
            prev && prev.id === projectId
                // project_services geleert: getNormalizedServices bevorzugt die
                // relationale Quelle, die hier nicht neu gelesen wurde.
                ? { ...prev, project_services: undefined, data: finalData }
                : prev,
        );
        onRefresh();
    };

    /**
     * Den Leistungsnachtrag eines Altbelegs zuruecknehmen, wenn der Abschlag
     * geloescht wird.
     *
     * Das Loeschen entfernte bisher nur die Zeile in external_advances — also
     * den BETRAG. Die Leistung, die derselbe Import ins Projekt geschrieben
     * hat (quantityUsed und quantityBilled je Position plus die
     * ALT-Protokollzeilen), blieb stehen. Wer eine versehentlich eingelesene
     * Alt-Rechnung wieder loeschte, hatte ihre Menge danach dauerhaft doppelt
     * im Leistungsstand, ohne Hinweis. Genau so entstanden in Projekt 26051007
     * aus 11 abgerechneten Begehungen 22.
     *
     * revertBackfill ist die exakte Umkehrung des Imports und arbeitet ueber
     * die Belegnummer. Gibt es zu ihr keinen Nachtrag, aendert es nichts — der
     * Aufruf ist dann folgenlos.
     *
     * Speicherweg wie beim Nachtrag: loadProjectById → saveProjectToCloud.
     */
    /**
     * Abgerechnete Menge OHNE Auftraggeber — der Altbestand, der jede
     * Teilrechnung sperrt.
     *
     * Aus dem Protokoll abgeleitet und nicht gespeichert: gespeichert waere er
     * eine zweite Wahrheit neben dem Protokoll, und genau daran entlang ist
     * dieses Modul gebaut, um so etwas zu vermeiden.
     */
    const unassignedBilled = useMemo(() => {
        const meta = selectedProjectForAccounting?.data?.metadata as ReportMetadata | undefined;
        const stands = billedByParty(meta?.globalData?.serviceLog || [], splitLegacy);
        let summe = 0;
        for (const stand of stands.values()) summe += stand.unassigned;
        return Math.round(summe * 1000) / 1000;
    }, [selectedProjectForAccounting, splitLegacy]);

    /**
     * Den Altbestand einem Auftraggeber zuschlagen.
     *
     * Angefasst werden NUR Zeilen ohne Kunden (assignUnassignedBilled). Eine
     * bereits zugeordnete Forderung auf einen anderen umzuschreiben waere ein
     * eigener Vorgang mit eigenem Beleg und passiert hier ausdruecklich nicht.
     */
    const handleAssignLegacyBilling = async (party: { partyId: string; customerId: string }) => {
        if (!selectedProjectForAccounting) return;
        const projectId = selectedProjectForAccounting.id;
        const fullProject = await loadProjectById(projectId);
        if (!fullProject) throw new Error('Projekt nicht mehr verfügbar.');
        const meta = fullProject.data.metadata as ReportMetadata;
        const globalData = meta.globalData || { status: 'active' as const };

        const { log, assigned } = assignUnassignedBilled(
            globalData.serviceLog || [],
            party,
            splitLegacy,
        );
        // Nichts zu tun: NICHT speichern. Ein leerer Save schriebe den hier
        // geladenen Stand ueber alles, was eine offene Sitzung inzwischen
        // gespeichert hat — dieselbe Regel wie beim Zuruecknehmen.
        if (assigned.size === 0) return;

        const performerName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin';
        const note = {
            id: crypto.randomUUID(),
            text: `Altbestand zugeordnet: ${assigned.size} Position(en) der bereits abgerechneten Menge gehören ab jetzt dem gewählten Auftraggeber. Grundlage der Aufteilung.`,
            date: new Date().toISOString(),
            author: performerName,
        };
        await saveProjectToCloud(
            {
                ...fullProject.data,
                metadata: {
                    ...meta,
                    globalData: {
                        ...globalData,
                        serviceLog: log,
                        notes: [note, ...(globalData.notes || [])],
                    },
                },
            } as Report,
            projectId,
        );
        onRefresh?.();
    };

    const handleRevertBackfill = async (invoiceNumber: string, customerId?: string) => {
        if (!selectedProjectForAccounting || !invoiceNumber.trim()) return;
        const projectId = selectedProjectForAccounting.id;
        const fullProject = await loadProjectById(projectId);
        if (!fullProject) throw new Error('Projekt nicht mehr verfügbar.');
        const meta = fullProject.data.metadata as ReportMetadata;
        const globalData = meta.globalData || { status: 'active' as const };

        const reverted = revertBackfill(
            globalData.services || [],
            globalData.serviceLog || [],
            invoiceNumber,
            new Set(),
            customerId,
        );
        // Kein Nachtrag zu dieser Belegnummer: nichts schreiben. Ein leerer
        // Save waere nicht harmlos — er schriebe den hier geladenen Stand ueber
        // alles, was eine offene Sitzung inzwischen gespeichert hat.
        if (reverted.revertedByService.size === 0) return;

        const performerName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin';
        const entfernt = reverted.removedServiceIds.length
            ? `, ${reverted.removedServiceIds.length} leergeräumte Position(en) entfernt`
            : '';
        const note = {
            id: crypto.randomUUID(),
            text: `Alt-Rechnung ${invoiceNumber} gelöscht: nachgetragene Menge auf ${reverted.revertedByService.size} Position(en) zurückgenommen${entfernt}.`,
            date: new Date().toISOString(),
            author: performerName,
        };
        const finalData = {
            ...fullProject.data,
            metadata: {
                ...meta,
                globalData: {
                    ...globalData,
                    services: reverted.services,
                    serviceLog: reverted.serviceLog,
                    notes: [note, ...(globalData.notes || [])],
                },
            },
        };

        // ─── Die ALT-Zeilen serverseitig entfernen — VOR dem Speichern ───────
        //
        // Dieselbe Regel wie beim erneuten Einlesen: ab Migration Nr. 66
        // verlaesst eine BU-/ALT-Zeile das Protokoll nur noch ueber
        // `service_log_remove_entries`, weil der Riegel `service_log_guard_trg`
        // sonst wirft (in OLD vorhanden, in NEW nicht mehr). Nach der RPC
        // fehlen sie bereits in OLD und der Save unten ist unauffaellig.
        //
        // `finalData` traegt `reverted.serviceLog`, also genau den Reststand
        // ohne diese Zeilen — der Save kann sie nicht wieder anlegen. Ein
        // echter Fehler wirft und wird vom Panel im Dialog gezeigt; `legacy`
        // laeuft wie bisher.
        await removeServiceLogEntries(
            projectId,
            reverted.removedEntryIds,
            `Alt-Rechnung ${invoiceNumber} gelöscht: nachgetragene Leistung zurückgenommen`,
        );

        await saveProjectToCloud(finalData, fullProject.id);

        // Ruecknahme im Buchungsjournal (S4c) — nach dem bestaetigten
        // Speichern. Das Loeschen des Abschlags nimmt den Leistungsnachtrag
        // zurueck: die ALT-Zeilen sind aus dem JSON heraus, im Journal
        // stuenden sie ohne diesen Schritt weiter und zaehlten mit.
        //
        // Nach der serverseitigen Entfernung, nicht davor: `voidJournalEntries`
        // kennt kein Un-void, eine vorgezogene Ruecknahme waere bei einem
        // Fehlschlag der Entfernung nicht mehr rueckgaengig zu machen. Eine
        // ueberzaehlige Journalzeile ist dagegen sichtbar und nachholbar.
        //
        // Eigener catch: der Aufrufer zeigt einen Fehlschlag als
        // fehlgeschlagene Ruecknahme, und die ist hier laengst geschrieben.
        try {
            await voidJournalEntries(projectId, reverted.removedEntryIds, 'backfill_revert');
        } catch (je) {
            console.error('[serviceJournal] Ruecknahme des Alt-Nachtrags fehlgeschlagen', je);
        }

        setSelectedProjectForAccounting((prev) =>
            prev && prev.id === projectId
                // project_services geleert: getNormalizedServices bevorzugt die
                // relationale Quelle, die hier nicht neu gelesen wurde.
                ? { ...prev, project_services: undefined, data: finalData }
                : prev,
        );
        onRefresh();
    };

    /**
     * Zuordnung eines Nachtrags nachtraeglich korrigieren.
     *
     * Fuer den Fall, den die Aehnlichkeitssuche im Import nicht abgefangen hat:
     * die Alt-Position wurde als Dublette angelegt, obwohl sie ins bestehende
     * LV gehoert. Verschoben werden die ALT-Protokollzeilen SAMT Menge; die
     * leergeraeumte Dublette faellt weg, sofern an ihr nachweislich nichts
     * anderes haengt (moveAltBackfill prueft das).
     *
     * Derselbe Speicherweg wie der Nachtrag — loadProjectById →
     * saveProjectToCloud.
     */
    const MOVE_ERROR_TEXT: Record<string, string> = {
        same_service: 'Quelle und Ziel sind dieselbe Position.',
        source_missing: 'Die Ursprungsposition ist nicht mehr im Projekt — bitte die Ansicht neu laden.',
        target_missing: 'Die Zielposition ist nicht mehr im Projekt — bitte die Ansicht neu laden.',
        no_alt_entries: 'An dieser Position hängt keine Buchung aus einer Alt-Rechnung — es gibt nichts zu verschieben.',
        nothing_to_move: 'Die Alt-Buchungen dieser Position tragen keine Menge mehr — es gibt nichts zu verschieben.',
        ambiguous_entries: 'Eine Alt-Buchung dieser Position ist im Protokoll nicht eindeutig gekennzeichnet (Kennung fehlt oder steht doppelt). Der Umzug würde fremde Einträge mitnehmen und wurde deshalb nicht ausgeführt — bitte zuerst die Daten bereinigen lassen.',
    };
    const KEPT_REASON_TEXT: Record<string, string> = {
        other_entries: 'an ihr hängen weitere Protokolleinträge',
        billed: 'an ihr steht eine bereits abgerechnete Menge',
        not_backfill: 'sie ist keine nachgetragene Altbestand-Position',
    };

    const handleMoveAltBackfill = async () => {
        if (!selectedProjectForAccounting || !correctionServiceId || !correctionTargetId || !darf.zuordnungKorrigieren.erlaubt) return;
        const projectId = selectedProjectForAccounting.id;
        try {
            const fullProject = await loadProjectById(projectId);
            if (!fullProject) throw new Error('Projekt nicht mehr verfügbar.');
            const meta = fullProject.data.metadata as ReportMetadata;
            const globalData = meta.globalData || { status: 'active' as const };
            const services = globalData.services || [];
            const source = services.find((s: ServiceItem) => s.id === correctionServiceId);
            const target = services.find((s: ServiceItem) => s.id === correctionTargetId);
            const result = moveAltBackfill(
                services,
                globalData.serviceLog || [],
                correctionServiceId,
                correctionTargetId,
            );
            if ('error' in result) {
                alert(MOVE_ERROR_TEXT[result.error] || 'Die Zuordnung konnte nicht korrigiert werden.');
                return;
            }
            const performerName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin';
            const numbers = result.invoiceNumbers.join(', ') || 'ohne Nummer';
            const fate = result.deletedServiceId
                ? 'Die leere Altbestand-Position wurde entfernt.'
                : `Die Ursprungsposition bleibt bestehen (${KEPT_REASON_TEXT[result.keptReason || ''] || 'unverändert'}).`;
            const note = {
                id: crypto.randomUUID(),
                text: `Zuordnung korrigiert: ${result.appliedQuantity} ${source?.unit || ''} aus Alt-Rechnung ${numbers} von „${source?.title || correctionServiceId}“ auf „${target?.title || correctionTargetId}“ übertragen. ${fate}`.replace(/\s+/g, ' '),
                date: new Date().toISOString(),
                author: performerName,
            };
            const finalData = {
                ...fullProject.data,
                metadata: {
                    ...meta,
                    globalData: {
                        ...globalData,
                        services: result.services,
                        serviceLog: result.serviceLog,
                        notes: [note, ...(globalData.notes || [])],
                    },
                },
            };
            await saveProjectToCloud(finalData, fullProject.id);
            // Zweitschrift im Journal — erst NACH dem bestaetigten Speichern:
            // die Journalzeile traegt die Position als Spalte, und ein Umzug,
            // der nur den Blob schreibt, liesse sie auf der Quelle stehen
            // (6290-1). Je umgezogener Zeile wird die alte Journalzeile ueber
            // ihre JOURNAL-id in EINER Transaktion durch die neue ersetzt
            // (SQL Nr. 136, backfillMoveJournal.ts) — nicht mehr anhaengen und
            // danach ueber die JSON-id stilllegen, was die falsche Zeile traf
            // (5633) oder nach A→B→A→B gar keine lebende uebrig liess (6263).
            // Wirft nie, und nichts davon geht in einen Dialog: das JSON ist
            // gespeichert, der Vorgang gelungen; eine Restabweichung zeigt der
            // Nachtlauf.
            if (result.movedEntryIds.length > 0) {
                await spiegleZuordnungsUmzug({
                    projectId: fullProject.id,
                    vorher: globalData.serviceLog || [],
                    nachher: result.serviceLog,
                    movedEntryIds: result.movedEntryIds,
                    linkedProjectConfig: globalData.linkedProjectConfig ?? null,
                });
            }
            setSelectedProjectForAccounting((prev) =>
                prev && prev.id === projectId
                    ? { ...prev, project_services: undefined, data: finalData }
                    : prev,
            );
            setCorrectionServiceId(null);
            setCorrectionTargetId('');
            onRefresh();
        } catch (e: any) {
            alert('Fehler beim Korrigieren der Zuordnung: ' + (e?.message || e));
        }
    };

    const handleDownloadReceipt = (receipt: any, project: Pick<ListProject, 'name' | 'data'>, e: React.MouseEvent) => {
        e.stopPropagation();
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.text('Buchungsbeleg', 14, 22);

        doc.setFontSize(11);
        doc.text(`Projekt: ${project.name}`, 14, 32);
        doc.text(`Projektnummer: ${project.data.metadata.projectNumber || '-'}`, 14, 38);
        doc.text(`Belegnummer: ${receipt.reference}`, 14, 44);
        doc.text(`Datum: ${new Date(receipt.date).toLocaleDateString('de-DE')}`, 14, 50);
        doc.text(`Verbucht von: ${receipt.bookedBy}`, 14, 56);

        const tableData = receipt.items.map((item: any) => [
            item.service?.title || 'Unbekannte Leistung',
            item.service?.lvPosition || '-',
            `${item.entry.quantity} ${item.service?.unit || ''}`,
            formatCurrency(item.service?.unitPrice || 0),
            formatCurrency(item.amount)
        ]);

        autoTable(doc, {
            startY: 65,
            head: [['Leistung', 'Position', 'Menge', 'Einzelpreis', 'Gesamt']],
            body: tableData,
            foot: [['', '', '', 'Summe:', formatCurrency(receipt.totalAmount)]],
            theme: 'striped',
            headStyles: { fillColor: [79, 70, 229] }, // Indigo 600
            footStyles: { fillColor: [248, 250, 252], textColor: [15, 23, 42] }
        });

        // Dateiname aus dem eindeutigen Gruppierungsschluessel: ueber reference
        // hiessen alle Altbeleg-PDFs gleich und ueberschrieben sich im Download.
        const fileKey = String(receipt.key ?? receipt.reference).replace(/[^\w.-]+/g, '_');
        doc.save(`Buchungsbeleg_${fileKey}_${project.data.metadata.projectNumber || 'Projekt'}.pdf`);
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
             {selectedProjectForAccounting && (
                <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-scale-in">
                        <div className="bg-indigo-600 text-white p-6 flex justify-between items-center gap-4 shrink-0">
                            <div className="min-w-0">
                                <h3 className="font-bold text-xl flex items-center gap-2 min-w-0"><Wallet className="w-6 h-6 shrink-0"/><span className="truncate" title={selectedProjectForAccounting.name}>Abrechnung: {selectedProjectForAccounting.name}</span>{nurLesen && <NurLesenPlakette aufFarbe />}</h3>
                                <p className="text-indigo-200 text-sm mt-1 truncate">{selectedProjectForAccounting.data.metadata.projectNumber}</p>
                            </div>
                            <div className="flex gap-2 items-center shrink-0">
                                {onOpenProject && (
                                    <Button variant="secondary" onClick={() => onOpenProject(selectedProjectForAccounting)} className="bg-white/10 hover:bg-white/20 text-white border-white/20" icon={<ArrowRight className="w-4 h-4"/>}>Projekt öffnen</Button>
                                )}
                                <Button variant="secondary" onClick={() => setShowBookingLog(true)} className="bg-white/10 hover:bg-white/20 text-white border-white/20" icon={<History className="w-4 h-4"/>}>Buchungshistorie</Button>
                                <button onClick={() => setSelectedProjectForAccounting(null)} className="hover:bg-white/10 p-2 rounded-full"><X className="w-6 h-6"/></button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
                                <div className="bg-slate-50 p-4 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider flex justify-between"><span>Positionen zur Abrechnung</span><span>Auswahl: {selectedServices.size}</span></div>
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs"><tr><th className="p-4 w-10"></th><th className="p-4">Leistung</th><th className="p-4 text-center">Vertrag</th><th className="p-4 text-center">Erledigt</th><th className="p-4 text-center">Bereits Abgerechnet</th><th className="p-4 text-right">Offen (Abrechenbar)</th></tr></thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {currentViewServices.map((s: ServiceItem) => {
                                            const billed = s.quantityBilled || 0, used = s.quantityUsed || 0;
                                            // Von einer Alt-Rechnung abgegoltene Menge zaehlt hier als
                                            // abgerechnet: sie wurde mit dem Altbeleg gefordert, auch wenn
                                            // quantityBilled sie (bewusst) nicht kennt. Ohne den Abzug
                                            // stuende dieselbe Leistung doppelt als "offen" da — genau die
                                            // Meldung, mit der die Buchhaltung hier ankommt.
                                            // quantityBilled zehrt die AELTESTEN Mengen auf, also die
                                            // Alt-Mengen: nur der noch nicht regulaer abgerechnete Rest
                                            // der Alt-Deckung darf hier abgezogen werden. Sonst gilt nach
                                            // der ersten kumulativen Rechnung jede NEUE Einheit dieser
                                            // Position als gedeckt und wird nie wieder abgerechnet.
                                            const alt = altCoverage.get(s.id);
                                            const altQty = alt?.quantity || 0;
                                            const effectiveAlt = Math.max(0, altQty - billed);
                                            // Verzicht: erbracht, wird aber nicht mehr gefordert.
                                            // Muss hier mit abgezogen werden — diese Zelle rechnet
                                            // eigenstaendig und nicht ueber openQuantity, weil sie
                                            // zusaetzlich die Alt-Deckung kennt.
                                            const waived = waivedQuantity(s);
                                            // MONATSPAUSCHALE: eine gedeckte Position (flatCoveredBy,
                                            // gestempelt in getNormalizedServices) hat NIE offenes Geld
                                            // — ihr Zuwachs ist mit der Pauschale abgegolten. Ohne den
                                            // Abzug stuende sie hier als "Offen (Abrechenbar)", der
                                            // Admin buchte sie von Hand, und die naechste kumulative
                                            // Rechnung froere den angehobenen Stand ein: dasselbe Geld
                                            // doppelt gefordert.
                                            // PAKETPOSITION: Gate und Deckel kommen aus derselben
                                            // Quelle (projectLv.accountingOpenQuantity → openQuantity) —
                                            // eine gesperrte Paketposition ist hier nie „abrechenbar".
                                            const openQty = accountingOpenQuantity(s, altQty), openAmount = openQty * s.unitPrice, isSelectable = openQty > 0 || used > 0;
                                            const paketGesperrt = istAbrechnungGesperrt(s);
                                            const paketHatUeberhang = !paketGesperrt && Math.max(0, used - billed - waived) > paketDeckel(s) + 0.0005;
                                            const zurueckgehalten = heldByComponentsQuantity(s);
                                            return (<tr key={s.id} className={`hover:bg-slate-50 transition-colors ${selectedServices.has(s.id) ? 'bg-indigo-50' : ''}`}>
                                                <td className="p-4"><input type="checkbox" disabled={!isSelectable} checked={selectedServices.has(s.id)} onChange={(e) => { const newSet = new Set(selectedServices); if (e.target.checked) newSet.add(s.id); else newSet.delete(s.id); setSelectedServices(newSet); }} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" /></td>
                                                <td className="p-4 font-medium text-slate-700">{s.title}<div className="text-[11px] text-slate-400 font-mono">{s.lvPosition}{s.clientLvPosition && <span className="ml-2 text-indigo-500">({s.clientLvPosition})</span>}</div>
                                                    {s.flatCoveredBy && (
                                                        <div className="mt-1 text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5 inline-block">
                                                            {currentViewFlatHints.get(s.id) || 'in der Monatspauschale enthalten'}
                                                        </div>
                                                    )}
                                                    {/* PAKETPOSITION: warum hier nichts abrechenbar ist —
                                                        ohne Bestandteilnamen, nur der Stand. */}
                                                    {paketGesperrt && (
                                                        <div
                                                            className="mt-1 text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-block"
                                                            title={zurueckgehalten > 0 ? `${zurueckgehalten} ${s.unit} erbracht, aber zurückgehalten, bis alle Bestandteile erledigt sind.` : 'Erst abrechenbar, wenn alle Bestandteile erledigt sind.'}
                                                        >
                                                            nicht abrechenbar: {bestandteilSperrText(s)}
                                                        </div>
                                                    )}
                                                    {paketHatUeberhang && (
                                                        <div className="mt-1 text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-block">
                                                            Paketposition: höchstens {paketDeckel(s)} {s.unit} abrechenbar (doppelt gebucht?)
                                                        </div>
                                                    )}
                                                    {/* Die Vorgaenge je Position schreiben alle in den
                                                        Projektstand. Im Lesemodus fallen sie weg statt
                                                        gesperrt dazustehen — vier graue Links je Zeile
                                                        waeren Rauschen; was gebucht ist, sagen die
                                                        Hinweise darunter weiter. */}
                                                    {altQty > 0 && darf.zuordnungKorrigieren.erlaubt && (
                                                        <button
                                                            type="button"
                                                            onClick={() => { setCorrectionServiceId(s.id); setCorrectionTargetId(''); }}
                                                            className="mt-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                                                            title="Die aus der Alt-Rechnung nachgetragene Menge auf eine andere Position übertragen."
                                                        >
                                                            Zuordnung korrigieren
                                                        </button>
                                                    )}
                                                    {/* Der Ausweg fuer den schlussgerechneten Fall:
                                                        eine erbrachte Menge, die nie auf einen Beleg
                                                        kam, bliebe sonst dauerhaft in dieser Liste. */}
                                                    {/* Auch bei einer zurueckgehaltenen Paketposition:
                                                        sonst waere der Verzicht — der eine Ausweg, den
                                                        der Archiv-Riegel nennt — genau dort weg, wo das
                                                        Archiv an ihr haengt. */}
                                                    {(openQty > 0 || waived > 0 || zurueckgehalten > 0) && darf.verzicht.erlaubt && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setWaiverServiceId(s.id)}
                                                            className="mt-1 block text-[11px] font-medium text-amber-600 hover:text-amber-800 underline underline-offset-2"
                                                            title="Diese Menge wird nicht mehr abgerechnet. Der Leistungsstand bleibt unveraendert."
                                                        >
                                                            {waived > 0 ? 'Verzicht bearbeiten' : 'Nicht mehr abrechnen'}
                                                        </button>
                                                    )}
                                                    {/* Der DRITTE Fall: abgerechnet wurde laengst, nur
                                                        fehlt der Beleg dazu. Steht neben dem Verzicht,
                                                        weil beide dieselbe offene Menge schliessen —
                                                        der eine, indem nicht mehr gefordert wird, der
                                                        andere, weil bereits gefordert wurde.
                                                        Eine flat-gedeckte Position bekommt den Einstieg
                                                        nicht angeboten: ihr Geldstand steht still, eine
                                                        Handbuchung hoebe quantityBilled an und die
                                                        naechste kumulative Rechnung forderte das von
                                                        der Pauschale abgegoltene Geld doppelt. Nur zum
                                                        BEARBEITEN bestehender MB-Buchungen bleibt der
                                                        Dialog erreichbar (Ruecknahme). */}
                                                    {((!s.flatCoveredBy && manuallyBillableQuantity(s) > 0) || (manualBilled.get(s.id) || 0) > 0) && darf.manuelleVerbuchung.erlaubt && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setManualBillingServiceId(s.id)}
                                                            className="mt-1 block text-[11px] font-medium text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                                                            title="Die Menge wurde in der Vergangenheit abgerechnet, der Beleg dazu fehlt."
                                                        >
                                                            {(manualBilled.get(s.id) || 0) > 0 ? 'Manuelle Verbuchung bearbeiten' : 'Als abgerechnet buchen'}
                                                        </button>
                                                    )}
                                                    {(manualBilled.get(s.id) || 0) > 0 && (
                                                        <div className="mt-1 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 inline-block">
                                                            {manualBilled.get(s.id)} {s.unit} von Hand ohne Beleg gebucht
                                                        </div>
                                                    )}
                                                    {/* Der andere Fall: die Buchung haette es nie
                                                        geben duerfen. Steht bewusst daneben und nicht
                                                        im selben Dialog. */}
                                                    {used > 0 && darf.ruecknahme.erlaubt && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setReversalServiceId(s.id)}
                                                            className="mt-1 block text-[11px] font-medium text-rose-600 hover:text-rose-800 underline underline-offset-2"
                                                            title="Die Leistung wurde nie erbracht (ausgefallen, Dublette, Zaehlerfehler). Der Leistungsstand sinkt."
                                                        >
                                                            Buchung zurücknehmen
                                                        </button>
                                                    )}
                                                    {waived > 0 && (
                                                        <div className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-block">
                                                            {waived} {s.unit} nicht mehr abgerechnet
                                                            {s.waivedReason && <span className="text-amber-600/80"> — {s.waivedReason}</span>}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-4 text-center text-slate-500">{isNepService(s) ? <span className="text-[11px] font-black text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded" title={`Eventualposition – Angebotsmenge ${s.quantityContracted} ${s.unit}, beauftragt ist nur das Gebuchte`}>{NEP_BADGE}</span> : `${s.quantityContracted} ${s.unit}`}</td>
                                                <td className="p-4 text-center font-bold text-slate-700">{used} {s.unit}</td>
                                                <td className="p-4 text-center text-emerald-600">{billed} {s.unit}
                                                    {/* Nur der Rest, den die regulaere Abrechnung noch nicht
                                                        erfasst hat — sonst staende dieselbe Menge zweimal da. */}
                                                    {effectiveAlt > 0 && (
                                                        <div className="text-[11px] text-slate-400 font-normal">
                                                            + {effectiveAlt} {s.unit} abgerechnet über Alt-Rechnung {alt?.invoiceNumbers.join(', ') || '—'}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-4 text-right font-mono font-bold text-indigo-600">{openQty > 0 ? formatCurrency(openAmount) : '-'}</td>
                                            </tr>);
                                        })}
                                        {currentViewServices.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400 italic">Keine Leistungen gefunden.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                            {/* Vorrechnungen: App-Belege + extern nachgetragene
                                Alt-Abschlaege — die Summe ist der Abzug der
                                naechsten kumulativen Rechnung. */}
                            <ExternalAdvancesPanel
                                projectId={selectedProjectForAccounting.id}
                                defaultTaxRateBp={defaultTaxRateBp ?? resolveInvoiceConfig((currentUser as any)?.settings?.invoiceConfig).defaultTaxRateBp}
                                projectServices={currentViewServices}
                                usedBackfillReferences={usedBackfillReferences}
                                // Im Lesemodus ohne Rueckrufe: beide schreiben ins Projekt.
                                onBackfillPositions={darf.vorrechnungen.erlaubt ? handleBackfillPositions : undefined}
                                onRevertBackfill={darf.vorrechnungen.erlaubt ? handleRevertBackfill : undefined}
                                splitParticipants={splitParticipants}
                                nurLesen={!darf.vorrechnungen.erlaubt}
                            />
                            <div className="flex justify-between items-center gap-3">
                                <div className="flex gap-3"><Button variant="secondary" onClick={handleOpenHistory} icon={<FileText className="w-4 h-4"/>}>Archiv / PDFs</Button></div>
                                <div className="flex gap-3">
                                    <Button variant="secondary" onClick={handleExportPerformance} icon={<Download className="w-4 h-4"/>}>Leistungsnachweis (PDF)</Button>
                                    {/* Aufteilungsschluessel: teilen sich mehrere Auftraggeber dieses
                                        Projekt, bekommt jeder eine eigene Rechnung. Der Schluessel gehoert
                                        VOR das Ausstellen und steht deshalb links davon — einmal
                                        ausgestellt, traegt jeder Beleg seine Aufteilung eingefroren, und
                                        eine spaetere Korrektur des Schluessels erreicht ihn nicht mehr. */}
                                    {/* Umstellung auf die Monatspauschale: gehoert wie der
                                        Aufteilungsschluessel VOR das Ausstellen — die Deckung
                                        bestimmt, was kuenftige Belege ueberhaupt noch fordern. */}
                                    <Button
                                        variant="secondary"
                                        onClick={() => setFlatSwitchOpen(true)}
                                        disabled={!darf.monatspauschale.erlaubt}
                                        title={darf.monatspauschale.hinweis ?? 'Dieses Projekt von der Abrechnung je Einzelposition auf eine Monatspauschale umstellen'}
                                        icon={<CalendarClock className="w-4 h-4"/>}
                                    >
                                        Monatspauschale
                                    </Button>
                                    {/* Bleibt im Lesemodus offen: den Schluessel ansehen ist
                                        Lesen. Der Editor sperrt dann jede Eingabe selbst. */}
                                    <Button
                                        variant="secondary"
                                        onClick={() => setSplitEditorOpen(true)}
                                        title={darf.aufteilungsschluessel.erlaubt
                                            ? 'Mehrere Auftraggeber teilen sich dieses Projekt'
                                            : `Aufteilungsschlüssel ansehen (${darf.aufteilungsschluessel.hinweis})`}
                                        icon={<Scale className="w-4 h-4"/>}
                                    >
                                        Aufteilungsschlüssel
                                    </Button>
                                    {/* Buchen laeuft seit dem Rechnungsmodul ausschliesslich ueber eine
                                        Rechnung: nur dort entsteht ein Beleg mit Nummer, Festschreibung und
                                        Archiv. Zwei Wege, die quantityBilled schreiben, waeren ein
                                        Doppelbuchungspfad — der Knopf bleibt sichtbar, damit klar ist, wohin
                                        die Funktion gewandert ist. */}
                                    <Button
                                        variant="primary"
                                        onClick={() => onBillProject?.(selectedProjectForAccounting.id)}
                                        disabled={!onBillProject || !darf.rechnungErstellen.erlaubt}
                                        title={darf.rechnungErstellen.hinweis ?? 'Rechnung für dieses Projekt anlegen'}
                                        icon={<CheckCircle className="w-4 h-4"/>}
                                    >
                                        Rechnung erstellen
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Die Dialoge der Schreibwege: im Lesemodus gar nicht erst —
                auch nicht ueber einen von frueher stehen gebliebenen Zustand. */}
            {reversalServiceId && selectedProjectForAccounting && darf.ruecknahme.erlaubt && (() => {
                const target = currentViewServices.find((s: ServiceItem) => s.id === reversalServiceId);
                if (!target) return null;
                const meta = selectedProjectForAccounting.data?.metadata as ReportMetadata | undefined;
                return (
                    <ServiceReversalDialog
                        service={target}
                        serviceLog={meta?.globalData?.serviceLog || []}
                        onReverse={handleReverse}
                        onClose={() => setReversalServiceId(null)}
                    />
                );
            })()}

            {manualBillingServiceId && selectedProjectForAccounting && darf.manuelleVerbuchung.erlaubt && (() => {
                const target = currentViewServices.find((s: ServiceItem) => s.id === manualBillingServiceId);
                if (!target) return null;
                const meta = selectedProjectForAccounting.data?.metadata as ReportMetadata | undefined;
                return (
                    <ManualBillingDialog
                        service={target}
                        serviceLog={meta?.globalData?.serviceLog || []}
                        splitParticipants={splitParticipants}
                        onBook={handleManualBilling}
                        onUndo={handleUndoManualBilling}
                        onClose={() => setManualBillingServiceId(null)}
                    />
                );
            })()}

            {waiverServiceId && selectedProjectForAccounting && darf.verzicht.erlaubt && (() => {
                const target = currentViewServices.find((s: ServiceItem) => s.id === waiverServiceId);
                if (!target) return null;
                const waiverMeta = selectedProjectForAccounting.data?.metadata as ReportMetadata | undefined;
                return (
                    <ServiceWaiverDialog
                        service={target}
                        serviceLog={waiverMeta?.globalData?.serviceLog || []}
                        onWaive={handleWaive}
                        onRelease={handleReleaseWaiver}
                        onClose={() => setWaiverServiceId(null)}
                    />
                );
            })()}

            {flatSwitchOpen && selectedProjectForAccounting && darf.monatspauschale.erlaubt && (
                <FlatRateSwitchAssistant
                    project={selectedProjectForAccounting}
                    currentUser={currentUser}
                    onBillProject={onBillProject}
                    // Nach der Umstellung wie bei Verzicht/Ruecknahme: Detail
                    // schliessen und neu laden — die Ansicht haengt an einem
                    // Abzug, der den neuen Stand noch nicht kennt.
                    onDone={() => { setFlatSwitchOpen(false); setSelectedProjectForAccounting(null); onRefresh(); }}
                    onClose={() => setFlatSwitchOpen(false)}
                />
            )}

            {splitEditorOpen && selectedProjectForAccounting && (
                <SplitKeyEditor
                    unassignedBilled={unassignedBilled}
                    // Ohne Rueckruf meldet der Editor den Altbestand nur.
                    onAssignLegacy={darf.aufteilungsschluessel.erlaubt ? handleAssignLegacyBilling : undefined}
                    nurLesen={!darf.aufteilungsschluessel.erlaubt}
                    projectId={selectedProjectForAccounting.id}
                    services={currentViewServices}
                    projectLabel={
                        selectedProjectForAccounting.name ||
                        selectedProjectForAccounting.project_number ||
                        undefined
                    }
                    onClose={() => setSplitEditorOpen(false)}
                />
            )}

            {showBookingLog && selectedProjectForAccounting && (
                <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden animate-scale-in">
                        <div className="bg-slate-800 text-white p-5 flex justify-between items-center shrink-0"><h3 className="font-bold text-lg flex items-center gap-2"><History className="w-5 h-5" /> Buchungsbelege & Historie</h3><button onClick={() => setShowBookingLog(false)} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5"/></button></div>
                        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 space-y-4">
                            {(() => {
                                const meta = selectedProjectForAccounting.data.metadata as ReportMetadata;
                                const log = meta.globalData?.serviceLog || [];
                                const services = meta.globalData?.services || [];
                                // Belege laut Journal-Buchungsart (Rechnung/Storno,
                                // manuelle Verbuchung), dazu die Nachbuchung
                                // 'MANUAL-': sie ist im Journal Arbeit und hat keine
                                // Buchungsart, gehoert in dieser Liste aber dazu.
                                const bookedEntries = log.filter(
                                    (e: ServiceTransaction) =>
                                        istBelegBuchung(e) || (e.bookingReference || '').startsWith('MANUAL-'),
                                );
                                
                                if (bookedEntries.length === 0) return <div className="p-8 text-center text-slate-400 italic bg-white rounded-xl border border-slate-200">Keine Buchungen vorhanden.</div>;

                                const groupedReceipts: Record<string, any> = {};
                                bookedEntries.forEach((entry: any) => {
                                    const service = services.find((s: any) => s.id === entry.serviceId);
                                    const amount = (entry.quantity || 0) * (service?.unitPrice || 0);
                                    const ref = entry.bookingReference || `LEGACY-${entry.bookedAt}`;
                                    
                                    if (!groupedReceipts[ref]) {
                                        groupedReceipts[ref] = {
                                            // Gruppierungsschluessel mitspeichern: reference ist
                                            // bei ALLEN Altbuchungen derselbe String. Als React-key,
                                            // Aufklapp-Vergleich und PDF-Dateiname wuerden sie sich
                                            // gegenseitig ueberschreiben — ein Klick klappte alle auf.
                                            key: ref,
                                            reference: entry.bookingReference || 'Legacy-Buchung',
                                            date: entry.bookedAt || entry.date,
                                            bookedBy: entry.bookedBy || entry.performerName || 'System',
                                            totalAmount: 0,
                                            items: []
                                        };
                                    }
                                    groupedReceipts[ref].totalAmount += amount;
                                    groupedReceipts[ref].items.push({ entry, service, amount });
                                });

                                const sortedReceipts = Object.values(groupedReceipts).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

                                return sortedReceipts.map((receipt) => {
                                    const isExpanded = expandedReceipt === receipt.key;
                                    const displayDate = new Date(receipt.date);
                                    
                                    return (
                                        <div key={receipt.key} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden transition-all">
                                            <div 
                                                className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 cursor-pointer hover:bg-slate-50"
                                                onClick={() => setExpandedReceipt(isExpanded ? null : receipt.key)}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="bg-indigo-50 p-3 rounded-lg text-indigo-600">
                                                        <FileText className="w-6 h-6" />
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-800 text-base">{receipt.reference}</div>
                                                        <div className="text-xs text-slate-500 flex items-center gap-2">
                                                            <span>{displayDate.toLocaleDateString('de-DE')} {displayDate.toLocaleTimeString('de-DE', {hour: '2-digit', minute:'2-digit'})}</span>
                                                            <span>•</span>
                                                            <span>Verbucht von: {receipt.bookedBy}</span>
                                                            <span>•</span>
                                                            <span>{receipt.items.length} Position(en)</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                                                    <div className="text-right">
                                                        <div className="text-[11px] text-slate-400 uppercase font-bold">Gesamtsumme</div>
                                                        <div className="font-mono font-bold text-emerald-600 text-lg">{formatCurrency(receipt.totalAmount)}</div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Button variant="secondary" onClick={(e) => handleDownloadReceipt(receipt, selectedProjectForAccounting, e)} className="h-8 text-xs bg-white" icon={<Download className="w-4 h-4"/>}>PDF</Button>
                                                        <div className="p-1 text-slate-400">
                                                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {isExpanded && (
                                                <div className="border-t border-slate-100 bg-slate-50/50 p-4">
                                                    <table className="w-full text-left text-sm">
                                                        <thead className="text-xs text-slate-500 uppercase font-bold border-b border-slate-200">
                                                            <tr>
                                                                <th className="pb-2">Leistung</th>
                                                                <th className="pb-2 text-center">Menge</th>
                                                                <th className="pb-2 text-right">Einzelpreis</th>
                                                                <th className="pb-2 text-right">Betrag</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100">
                                                            {receipt.items.map((item: any, idx: number) => (
                                                                <tr key={idx} className="hover:bg-white transition-colors">
                                                                    <td className="py-3">
                                                                        <div className="font-medium text-slate-800">{item.service?.title || 'Unbekannte Leistung'}</div>
                                                                        <div className="text-[11px] text-slate-500 font-mono">{item.service?.lvPosition}</div>
                                                                    </td>
                                                                    <td className="py-3 text-center font-bold text-slate-700">{item.entry.quantity} {item.service?.unit}</td>
                                                                    <td className="py-3 text-right text-slate-500">{formatCurrency(item.service?.unitPrice || 0)}</td>
                                                                    <td className="py-3 text-right font-mono text-emerald-600 font-medium">{formatCurrency(item.amount)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end"><Button variant="secondary" onClick={() => setShowBookingLog(false)}>Schließen</Button></div>
                    </div>
                </div>
            )}

            {/* Zuordnung eines Nachtrags korrigieren: die ALT-Buchungen samt
                Menge auf die richtige Position umhaengen. */}
            {correctionServiceId && selectedProjectForAccounting && darf.zuordnungKorrigieren.erlaubt && (() => {
                const source = currentViewServices.find((s: ServiceItem) => s.id === correctionServiceId);
                const alt = altCoverage.get(correctionServiceId);
                return (
                    <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in flex flex-col">
                            <div className="bg-slate-800 text-white p-5 flex justify-between items-center shrink-0">
                                <h3 className="font-bold text-lg">Zuordnung korrigieren</h3>
                                <button onClick={() => { setCorrectionServiceId(null); setCorrectionTargetId(''); }} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5" /></button>
                            </div>
                            <div className="p-6 space-y-4">
                                <p className="text-sm text-slate-600">
                                    {/* Gedeckelt auf die erledigte Menge der Quelle: mehr kann sie nicht
                                        abgeben, ohne dass beim Umhängen Einheiten entstehen. */}
                                    Aus „{source?.title || 'unbekannte Position'}“ werden {Math.min(alt?.quantity || 0, Math.max(0, source?.quantityUsed || 0))} {source?.unit || ''} aus
                                    Alt-Rechnung {alt?.invoiceNumbers.join(', ') || '—'} auf die gewählte Position übertragen — mit den
                                    dazugehörigen Protokolleinträgen.
                                </p>
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Zielposition</label>
                                    <select
                                        value={correctionTargetId}
                                        onChange={(e) => setCorrectionTargetId(e.target.value)}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">Bitte wählen …</option>
                                        {currentViewServices
                                            .filter((s: ServiceItem) => s.id !== correctionServiceId)
                                            .map((s: ServiceItem) => (
                                                <option key={s.id} value={s.id}>{[s.lvPosition, s.title].filter(Boolean).join(' · ')}</option>
                                            ))}
                                    </select>
                                </div>
                                <p className="text-[11px] text-slate-400">
                                    Preis und bereits abgerechnete Menge der Zielposition bleiben unverändert. Die Ursprungsposition wird nur
                                    entfernt, wenn sie eine nachgetragene Altbestand-Position ohne weitere Einträge und ohne abgerechnete Menge ist.
                                </p>
                            </div>
                            <div className="p-4 bg-slate-50 border-t flex justify-end gap-3 shrink-0">
                                <Button variant="ghost" onClick={() => { setCorrectionServiceId(null); setCorrectionTargetId(''); }}>Abbrechen</Button>
                                <Button variant="primary" onClick={() => void handleMoveAltBackfill()} disabled={!correctionTargetId}>Übertragen</Button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Performance Proof Editor */}
            {proofEditorProject && (
                <PerformanceProofEditor isOpen={true} onClose={() => setProofEditorProject(null)} project={proofEditorProject} userProfile={currentUser} onRefresh={onRefresh} nurLesen={!darf.nachweisEintraege.erlaubt} />
            )}

            {billingModalOpen && !nurLesen && (
                <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in flex flex-col max-h-[80vh]">
                        <div className="bg-indigo-600 text-white p-5 flex justify-between items-center shrink-0"><h3 className="font-bold text-lg flex items-center gap-2"><Wallet className="w-5 h-5" /> Buchung bestätigen</h3><button onClick={() => setBillingModalOpen(false)} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5"/></button></div>
                        <div className="p-6 overflow-y-auto">
                            <div className="mb-4"><label className="text-xs font-bold text-slate-500 uppercase block mb-1">Datum</label><input type="date" value={billingDate} onChange={(e) => setBillingDate(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"/></div>
                            <div className="space-y-4">
                                {getNormalizedServices(selectedProjectForAccounting).filter((s: ServiceItem) => selectedServices.has(s.id)).map((s: ServiceItem) => {
                                    // Dieselbe Regel wie ueberall: openQuantity kennt
                                    // Verzicht UND Monatspauschale — used - billed kannte
                                    // beides nicht und boete gedecktes Geld zum Buchen an.
                                    const maxVal = openQuantity(s);
                                    return (<div key={s.id} className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                        <div className="flex justify-between items-start mb-2"><div><div className="font-bold text-sm text-slate-800">{s.title}</div><div className="text-xs text-slate-500 font-mono">{s.lvPosition}{s.clientLvPosition && <span className="ml-1 text-indigo-500">({s.clientLvPosition})</span>}</div></div><span className="text-[11px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Offen: {maxVal} {s.unit}</span></div>
                                        <div className="flex items-center gap-3"><label className="text-xs font-bold text-indigo-600">Zu verbuchen:</label><input type="number" min="0" max={maxVal} step={isHourlyUnit(s.unit) ? 0.25 : 1} value={billingValues[s.id] || 0} onChange={(e) => { const val = parseFloat(e.target.value); if (!isNaN(val)) setBillingValues(prev => ({ ...prev, [s.id]: Math.min(Math.max(0, val), maxVal) })); }} className="w-24 px-2 py-1 text-sm border border-indigo-200 rounded outline-none font-bold text-center focus:ring-indigo-500" /><span className="text-xs font-medium text-slate-600">{s.unit}</span></div>
                                    </div>);
                                })}
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 border-t flex justify-end gap-3 shrink-0"><Button variant="ghost" onClick={() => setBillingModalOpen(false)}>Abbrechen</Button><Button variant="primary" onClick={handleExecuteBilling} className="bg-indigo-600 hover:bg-indigo-700">Jetzt Buchen</Button></div>
                    </div>
                </div>
            )}
            
            {historyModalOpen && (
                <div className="fixed inset-0 z-[250] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
                        <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0"><h3 className="font-bold">Projekt-Archiv</h3><button onClick={() => setHistoryModalOpen(false)}><X className="w-5 h-5"/></button></div>
                        {historyHinweis && <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-xs px-4 py-2 shrink-0">{historyHinweis}</div>}
                        <div className="flex-1 overflow-hidden"><HistoryView history={projectHistory} onLoad={() => {}} readOnly={!darf.archivLoeschen.erlaubt} onDelete={async (id) => {
                            if (!darf.archivLoeschen.erlaubt) return;
                            const success = await deleteReportHistory(id);
                            if (success) {
                                setProjectHistory(prev => prev.filter(h => h.id !== id));
                            } else {
                                alert('Fehler beim Löschen des Eintrags.');
                            }
                        }} onGeneratePdf={(r) => { setPreviewReport(r); setShowPdfPreview(true); }} embedded={true} /></div>
                    </div>
                </div>
            )}
            {showPdfPreview && previewReport && <PdfPreviewModal report={previewReport} settings={previewSettings} isReadOnly={nurLesen} onClose={() => { setShowPdfPreview(false); setPreviewReport(null); }} />}

            <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div><h3 className="font-bold text-lg text-slate-800 flex items-center gap-2"><Wallet className="w-5 h-5 text-indigo-600"/> Abrechnungs-Übersicht{nurLesen && <NurLesenPlakette />}</h3><p className="text-sm text-slate-500 mt-1">Überblick über offene Posten und abgerechnete Leistungen.</p></div>
                <div className="flex gap-2 items-center">
                    <div className="flex bg-white rounded-xl border border-slate-200 p-1 shadow-sm"><div className="flex items-center px-2 gap-2 border-r border-slate-100"><Search className="w-4 h-4 text-slate-400" /></div><input type="text" placeholder="Projekt suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="bg-transparent text-xs py-1 px-2 outline-none w-40" /></div>
                    {!showAccountingArchive && (
                        <div className="flex gap-2 items-center bg-white p-1 rounded-lg border border-slate-200 shadow-sm mr-2">
                            <div className="flex items-center px-2 gap-2 border-r border-slate-100"><Filter className="w-4 h-4 text-slate-400" /><span className="text-xs font-bold text-slate-600 uppercase">Filter:</span></div>
                            <label className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-slate-50 rounded"><input type="checkbox" checked={onlyBillableFilter} onChange={(e) => setOnlyBillableFilter(e.target.checked)} className="rounded text-indigo-600 focus:ring-indigo-500" /><span className="text-xs font-medium text-slate-600">Nur Abrechenbare</span></label>
                            <div className="flex items-center gap-2 px-2"><span className="text-xs font-medium text-slate-600">Ab Summe:</span><input type="number" value={minBillableFilter} onChange={(e) => setMinBillableFilter(Number(e.target.value))} className="w-20 px-2 py-1 text-xs border rounded bg-slate-50 outline-none focus:border-indigo-500" /><span className="text-xs font-medium text-slate-600">€</span></div>
                        </div>
                    )}
                    <Button variant="secondary" onClick={() => setShowAccountingArchive(!showAccountingArchive)} className={`text-xs h-8 ${showAccountingArchive ? 'bg-slate-200 text-slate-800' : 'bg-white text-slate-600'}`} icon={<History className="w-3 h-3"/>}>{showAccountingArchive ? 'Offene Posten' : 'Archiv / Historie'}</Button>
                </div>
            </div>

            {!showAccountingArchive ? (
                <>
                    {(() => {
                        const totalContractSum = filteredAccountingProjects.reduce((acc, p) => acc + p.financials.total, 0);
                        const totalBilledSum = filteredAccountingProjects.reduce((acc, p) => acc + p.financials.billed, 0);
                        // Angezeigt wird displayOpen: von einer Alt-Rechnung
                        // abgegoltene Mengen sind gefordert, auch wenn
                        // quantityBilled sie nicht kennt. GEFILTERT und SORTIERT
                        // wird weiter ueber financials.open — an ihm haengt,
                        // welche Projekte ueberhaupt in der Liste stehen, und
                        // eine Anzeigekorrektur darf nichts unsichtbar machen.
                        const totalOpenSum = filteredAccountingProjects.reduce((acc, p) => acc + p.financials.displayOpen, 0);
                        return (
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs border-b border-slate-200"><tr><th className="p-4">Projekt</th><th className="p-4 text-right"><div>Vertragsvolumen</div><div className="text-[11px] text-slate-400 font-mono mt-0.5">{formatCurrency(totalContractSum)}</div></th><th className="p-4 text-right"><div>Bereits Gebucht</div><div className="text-[11px] text-emerald-600/70 font-mono mt-0.5">{formatCurrency(totalBilledSum)}</div></th><th className="p-4 text-right text-indigo-600"><div>Offen (Abrechenbar)</div><div className="text-[11px] text-indigo-400 font-mono mt-0.5">{formatCurrency(totalOpenSum)}</div></th></tr></thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredAccountingProjects.map(p => {
                                        const isFinished = isProjectFinished(p);
                                        const isArchivedOpen = isProjectArchived(p);
                                        const finalStand = isArchivedOpen
                                            ? offenerBelegStand(offeneBelege?.get(p.id))
                                            : isFinished ? finalInvoiceStandFor(p.id) : null;
                                        const finalHint = finalStand ? (isArchivedOpen ? ARCHIV_BELEG_HINT : FINAL_STAND_HINT[finalStand.stufe]) : undefined;
                                        const openAccounting = () => { setSelectedProjectForAccounting(p); setSelectedServices(new Set()); };
                                        // Die ganze Zeile oeffnet die Abrechnung, eine eigene
                                        // Aktionsspalte gibt es nicht. Per Tastatur: Tab + Enter.
                                        return (<tr key={p.id} className="hover:bg-slate-50 cursor-pointer group focus:outline-none focus-visible:bg-indigo-50/50" tabIndex={0} onClick={openAccounting} onKeyDown={(e) => { if (e.key === 'Enter') openAccounting(); }}>
                                            <td className="p-4"><div className="font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">{p.name}{isFinished && <span className="ml-2 text-[11px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200 uppercase font-bold" title={isArchivedOpen ? 'Archiviertes Projekt mit offenem Geld (Altbestand)' : 'Projekt beendet — Schlussrechnung stellen'}>{isArchivedOpen ? 'Archiv (offen)' : 'Schlussrechnung'}</span>}</div><div className="text-xs text-slate-500">{p.data.metadata.projectNumber}</div></td>
                                            <td className="p-4 text-right font-mono text-slate-500">{formatCurrency(p.financials.total)}</td>
                                            <td className="p-4 text-right font-mono text-emerald-600 font-medium">{formatCurrency(p.financials.billed)}</td>
                                            <td className="p-4 text-right font-mono font-bold text-indigo-600">{p.financials.displayOpen > 0 ? <span className="bg-indigo-50 px-2 py-1 rounded border border-indigo-100">{formatCurrency(p.financials.displayOpen)}</span> : finalStand ? (
                                                    <span className={`inline-block font-sans text-[11px] font-medium px-2 py-1 rounded border ${FINAL_STAND_STYLE[finalStand.stufe]}`} title={finalHint}>{finalStand.text}</span>
                                                ) : <span className="text-slate-300">-</span>}
                                                {/* BEENDET mit offenem Betrag: der Betrag bleibt vorn, der
                                                    Stand der Schlussrechnung steht klein darunter. */}
                                                {p.financials.displayOpen > 0 && finalStand && (
                                                    <div className="mt-1 font-sans text-[11px] font-normal text-slate-500" title={finalHint}>{finalStand.text}</div>
                                                )}
                                                {/* Die Zeile bleibt sichtbar (Filter rechnen mit open),
                                                    der Betrag ist aber um die Alt-Deckung bereinigt —
                                                    ohne diesen Hinweis saehe das nach einem Fehler aus. */}
                                                {p.financials.displayOpen < p.financials.open && (
                                                    <div className="text-[11px] font-normal text-slate-400">
                                                        {formatCurrency(p.financials.open - p.financials.displayOpen)} über Alt-Rechnung abgerechnet
                                                    </div>
                                                )}
                                                {/* PAKETPOSITION: erbracht, aber zurueckgehalten — steht
                                                    NICHT im Betrag oben (nicht abrechenbar), darf aber
                                                    auch nicht verschwinden. */}
                                                {p.financials.heldByComponents > 0 && (
                                                    <div className="text-[11px] font-normal text-amber-700" title="Paketpositionen, deren Bestandteile noch nicht alle erledigt sind">
                                                        {formatCurrency(p.financials.heldByComponents)} zurückgehalten (Bestandteile offen)
                                                    </div>
                                                )}
                                            </td>
                                        </tr>);
                                    })}
                                    {filteredAccountingProjects.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-400 italic">Keine Projekte entsprechen den Filterkriterien.</td></tr>}
                                </tbody>
                            </table>
                        );
                    })()}
                </>
            ) : (
                <div className="space-y-8 animate-fade-in">
                    {finishedProjects.length > 0 && (
                        <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                            <div className="p-4 border-b border-slate-200 bg-slate-100/50"><h4 className="font-bold text-slate-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600"/> Archivierte Projekte (Schlussrechnung gestellt und bezahlt)</h4></div>
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs border-b border-slate-200"><tr><th className="p-4">Projekt</th><th className="p-4 text-right">Vertragsvolumen</th><th className="p-4 text-right">Abgerechnet</th><th className="p-4 text-center">Status</th><th className="p-4 text-center">Aktion</th></tr></thead>
                                <tbody className="divide-y divide-slate-100">
                                    {finishedProjects.map(p => (<tr key={p.id} className="hover:bg-white transition-colors"><td className="p-4"><div className="font-bold text-slate-800">{p.name}</div><div className="text-xs text-slate-500">{p.data.metadata.projectNumber}</div></td><td className="p-4 text-right font-mono text-slate-500">{formatCurrency(p.financials.total)}</td><td className="p-4 text-right font-mono text-emerald-600 font-bold">{formatCurrency(p.financials.billed)}</td><td className="p-4 text-center"><span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-bold border border-slate-200 uppercase">Archiv</span></td><td className="p-4 text-center"><Button variant="secondary" size="sm" className="whitespace-nowrap" title="Abrechnung des archivierten Projekts öffnen" onClick={() => { setSelectedProjectForAccounting(p); setSelectedServices(new Set()); }}>Öffnen<ArrowRight className="w-3.5 h-3.5 shrink-0" /></Button></td></tr>))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                         <div className="p-4 border-b border-slate-200 bg-slate-50"><h4 className="font-bold text-slate-700 flex items-center gap-2"><History className="w-4 h-4 text-slate-500"/> Verbuchte Positionen (Historie)</h4></div>
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs border-b border-slate-200"><tr><th className="p-4">Projekt</th><th className="p-4">Leistung / Position</th><th className="p-4 text-center">Menge (Gebucht)</th><th className="p-4 text-right">Betrag (Gebucht)</th><th className="p-4 text-center">Aktion</th></tr></thead>
                            <tbody className="divide-y divide-slate-100">
                                {archivedPositions.map((item) => (<tr key={item.uniqueId} className="hover:bg-slate-50 group"><td className="p-4"><div className="font-bold text-slate-800">{item.projectName}</div><div className="text-xs text-slate-500">{item.projectNumber}</div></td><td className="p-4"><div className="text-slate-700 font-medium">{item.service.title}</div><div className="text-xs text-slate-400 font-mono">{item.service.lvPosition}{item.service.clientLvPosition && <span className="ml-1 text-indigo-500">({item.service.clientLvPosition})</span>}</div></td><td className="p-4 text-center"><span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded font-bold text-xs border border-emerald-100">{item.billedQty} {item.service.unit}</span></td><td className="p-4 text-right font-mono font-bold text-emerald-600">{formatCurrency(item.billedAmount)}</td><td className="p-4 text-center">{String(item.service.id).length > 0 && (item.hasInvoiceBooking ? (
                                    // Ueber eine Rechnung gebuchte Positionen duerfen hier nicht
                                    // zurueckgesetzt werden: dazu gehoert ein Stornobeleg, sonst
                                    // widersprechen Projekt und Rechnung einander.
                                    <span className="text-xs text-slate-400" title="Diese Position wurde über eine Rechnung gebucht. Zurücknehmen nur per Storno im Rechnungsmodul.">nur per Storno</span>
                                ) : (
                                    <button onClick={() => handleRestorePosition(item.projectId, item.service.id)} disabled={!darf.altbuchungStornieren.erlaubt} className="text-slate-400 hover:text-orange-600 p-1.5 rounded hover:bg-orange-50 transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400" title={darf.altbuchungStornieren.hinweis ?? 'Wiederherstellen (Storno) – nur für Altbuchungen vor dem Rechnungsmodul'}><RotateCcw className="w-4 h-4"/></button>
                                ))}</td></tr>))}
                                {archivedPositions.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400 italic">Keine verbuchten Positionen im Archiv.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};